import { Graph, Edge } from 'graphlib';
import { IDataInstance } from '../data-instance/interfaces';
import { type PositionalConstraintError, type GroupOverlapError, type HiddenNodeConflictError, type IConstraintValidator, isPositionalConstraintError, isGroupOverlapError, isHiddenNodeConflictError } from './constraint-types';
import { EdgeStyle, normalizeEdgeStyle } from './edge-style';
import type { SelectorErrorDetail, LayoutWarning } from './error-state';


import {
    LayoutNode, LayoutEdge, LayoutConstraint, InstanceLayout,
    LeftConstraint, TopConstraint, AlignmentConstraint, LayoutGroup,
    ImplicitConstraint, DisjunctiveConstraint, isLeftConstraint, isTopConstraint, isAlignmentConstraint,
    negateDisjunction, ColorSource
} from './interfaces';

import {
    LayoutSpec,
    RelativeOrientationConstraint, CyclicOrientationConstraint,
    RelativeDirection,
    GroupBySelector, AlignConstraint,
    EdgeColorDirective, InferredEdgeDirective,
    ProvidedSource, providedSourceHTML
} from './layoutspec';
import { resolveEdgeStyle } from './style/edge-style-spec';
import type { EdgeStyleSpec, LineStyle } from './style/edge-style-spec';
import { resolveAtomStyle, iconToAtomStyleRule } from './style/atom-style-spec';
import type { AtomStyleRule, AtomStyleSpec } from './style/atom-style-spec';
import type { TextStyle } from './style/text-style';


import IEvaluator from '../evaluator-contracts';
import { SelectorArityError } from '../evaluator-contracts';
import type { IEvaluatorResult } from '../evaluator-contracts';
import { ColorPicker } from './colorpicker';
import { type ConstraintError, type ErrorMessages } from './constraint-types';
import { QualitativeConstraintValidator } from './qualitative-constraint-validator';
import { estimateLabelBox, resolveAttrFontSize, SecondaryLine } from './text-extent';

/** The strings the renderer will draw inside a node's box. Used to size the box. */
type NodeDisplayContent = {
    label: string;
    /** One per attribute key, formatted "key: v1, v2, ..." to match the renderer. */
    attributeLines: string[];
    /** Pixel font size for each entry in {@link attributeLines} (same order/length). */
    attributeFontSizes: number[];
    /** One per Skolem-label key, formatted as "v1, v2, ..." (key not shown by renderer). */
    skolemLines: string[];
};
const UNIVERSAL_TYPE = "univ";

type ConstraintSource = RelativeOrientationConstraint | CyclicOrientationConstraint | AlignConstraint | ImplicitConstraint;

/**
 * Reduces a constraint's `toHTML()` output to plain text.
 *
 * `toHTML()` exists to be injected into the error modal's markup, so it wraps
 * selectors in `<code>`. Warning labels are consumed as *text* — rendered with
 * textContent, logged to a console, asserted in a test — so the tags have to go,
 * or every consumer sees a literal `<code>next</code>`. Stripping here rather
 * than at each render point keeps the label usable everywhere.
 */
/**
 * Spec types written under `constraints:` rather than `directives:`, so a warning
 * can call the offending item by the name its author used. Mirrors the
 * `kind: 'constraint'` classification in `src/spec-editor/core/registry.ts` —
 * note `size` and `hideAtom` are authored as constraints even though
 * `parseLayoutSpec` files them under `directives`.
 */
const CONSTRAINT_SPEC_TYPES: ReadonlySet<string> = new Set([
    'orientation', 'cyclic', 'align', 'group', 'size', 'hideAtom'
]);

/**
 * Composes the user-facing text for an evaluator diagnostic.
 *
 * Written here rather than passed through from simple-graph-query: sgq's own
 * message has to stand alone for any consumer, so it re-explains the empty-set
 * semantics and how to write a string literal. We already show the offending
 * item's label above the message and the selector below it, so most of that is
 * redundant here. What the spec author actually needs is the name that missed
 * and the consequence.
 */
function warningMessage(
    d: { kind: string; name?: string; message: string },
    specType: string
): string {
    if (d.kind !== 'unresolved-name' || !d.name) {
        return d.message;
    }
    const noun = CONSTRAINT_SPEC_TYPES.has(specType) ? 'constraint' : 'directive';
    return `'${d.name}' did not match any type, relation, or atom. `
        + `This ${noun} does not apply to anything.`;
}

function stripHtml(html: string): string {
    return html
        .replace(/<[^>]*>/g, '')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Result of layout generation. When constraints are unsatisfiable, `layout`
 * contains a counterfactual diagram built from the maximal feasible subset
 * of constraints, annotated with error metadata (conflictingConstraints,
 * overlappingNodes) for visual highlighting. `error` is non-null on conflict.
 */
type CounterfactualLayoutResult = {
    layout: InstanceLayout;
    error: ConstraintError | null;
    selectorErrors: SelectorErrorDetail[];
    /**
     * Advisory notes about individual constraints and directives — a selector
     * naming something that does not exist, say. The layout still rendered; each
     * warning explains one item that quietly had no effect. Separate from
     * `selectorErrors` so that list keeps its errors-only meaning for existing
     * consumers.
     */
    warnings: LayoutWarning[];
    /** The qualitative constraint validator, if used. Exposes modal spatial queries (must/can/cannot). */
    validator?: QualitativeConstraintValidator;
};

class MissingNodeConstraintError extends Error implements ConstraintError {
    readonly type = 'unknown-constraint';
    readonly missingNodeId: string;
    readonly sourceConstraint?: ConstraintSource;

    constructor(missingNodeId: string, sourceConstraint?: ConstraintSource) {
        const selector = sourceConstraint && 'selector' in sourceConstraint ? sourceConstraint.selector : undefined;
        const selectorDetails = selector ? ` Selector: ${selector}.` : '';
        super(`Constraint references node "${missingNodeId}" that is missing from the layout (it may be hidden).${selectorDetails}`);
        this.name = 'MissingNodeConstraintError';
        this.missingNodeId = missingNodeId;
        this.sourceConstraint = sourceConstraint;
        Object.setPrototypeOf(this, MissingNodeConstraintError.prototype);
    }
}

function isMissingNodeConstraintError(error: unknown): error is MissingNodeConstraintError {
    return error instanceof MissingNodeConstraintError;
}

/**
 * Strategy for adding alignment edges to prevent WebCola from falling into bad local minima.
 * 
 * - `never`: Never add alignment edges (maximum performance, may result in suboptimal layouts)
 * - `direct`: Only add alignment edges when nodes have no direct edge between them
 * - `connected`: Only add alignment edges when nodes are not connected via any path (default, best balance)
 */
export enum AlignmentEdgeStrategy {
    /** Never add alignment edges - maximum performance but may result in poor layouts */
    NEVER = 'never',
    /** Add alignment edges only when no direct edge exists between nodes */
    DIRECT = 'direct',
    /** Add alignment edges only when nodes are not connected via any path (default) */
    CONNECTED = 'connected'
}

/**
 * Strategy for which constraint validator to use.
 *
 * - `qualitative` (default): CDCL-based validator that reasons over qualitative
 *   partial-order constraints before handing off to Kiwi for coordinate
 *   assignment. Faster on large/group-heavy instances with geometry-aware
 *   pruning and clause learning.
 *
 * The deprecated `kiwi` strategy was removed in 6.0.0 along with the validator
 * it selected. One member remains so the constructor parameter keeps its
 * meaning for callers that pass it.
 */
export enum ConstraintValidatorStrategy {
    /** CDCL-based qualitative validator (default) */
    QUALITATIVE = 'qualitative'
}


// Should create a NEW list when it returns.
function removeDuplicateConstraints(constraints: LayoutConstraint[]): LayoutConstraint[] {
    const uniqueConstraints: LayoutConstraint[] = [];
    const seen = new Set<string>();
    
    for (const constraint of constraints) {
        let key: string;
        
        if (isLeftConstraint(constraint)) {
            // For left constraints: left_node_id|right_node_id|minDistance
            key = `left|${constraint.left.id}|${constraint.right.id}|${constraint.minDistance}`;
        } else if (isTopConstraint(constraint)) {
            // For top constraints: top_node_id|bottom_node_id|minDistance
            key = `top|${constraint.top.id}|${constraint.bottom.id}|${constraint.minDistance}`;
        } else if (isAlignmentConstraint(constraint)) {
            // For alignment constraints: axis|node1_id|node2_id (order normalized)
            const [node1, node2] = [constraint.node1.id, constraint.node2.id].sort();
            key = `align|${constraint.axis}|${node1}|${node2}`;
        } else {
            // Fallback for unknown constraint types - include all in case they're different
            key = `unknown|${JSON.stringify(constraint)}`;
        }
        
        if (!seen.has(key)) {
            seen.add(key);
            uniqueConstraints.push(constraint);
        }
    }
    
    return uniqueConstraints;
}

/**
 * Produces a natural-language description for an orientation constraint tuple that
 * conflicts with a hidden node. Orientation `directions` place the TARGET relative
 * to the SOURCE (`right` on tuple (src, tgt) puts tgt to the right of src), so the
 * description leads with the target — this must agree with what the counterfactual
 * diagram actually draws.
 */
function describeDroppedOrientationConstraint(sourceNodeId: string, targetNodeId: string, direction: string): string {
    switch (direction) {
        case 'left':
            return `${targetNodeId} must be to the left of ${sourceNodeId}`;
        case 'right':
            return `${targetNodeId} must be to the right of ${sourceNodeId}`;
        case 'above':
            return `${targetNodeId} must be above ${sourceNodeId}`;
        case 'below':
            return `${targetNodeId} must be below ${sourceNodeId}`;
        case 'directlyLeft':
            return `${targetNodeId} must be directly to the left of ${sourceNodeId}`;
        case 'directlyRight':
            return `${targetNodeId} must be directly to the right of ${sourceNodeId}`;
        case 'directlyAbove':
            return `${targetNodeId} must be directly above ${sourceNodeId}`;
        case 'directlyBelow':
            return `${targetNodeId} must be directly below ${sourceNodeId}`;
        default:
            return `${targetNodeId} is constrained relative to ${sourceNodeId} (${direction})`;
    }
}

class LayoutNodePath {
    constructor(
        public Path: LayoutNode[],
        public LoopsTo: LayoutNode | undefined
    ) { }

    /**
     * Expands the path by unrolling the loop `repeat` times.
     * If there is no loop, returns the plain path.
     */
    expand(repeat: number): string[] {
        const ids = this.Path.map(n => n.id);

        if (!this.LoopsTo) return ids;

        const loopStart = ids.findIndex(id => id === this.LoopsTo!.id);
        if (loopStart === -1) return ids;

        const prefix = ids.slice(0, loopStart);
        const loop = ids.slice(loopStart);
        return prefix.concat(...Array(repeat).fill(loop));
    }

    /**
     * Returns true if `this` is a superpath that contains `other` as a subpath (after unrolling).
     */
    isSubpathOf(other: LayoutNodePath): boolean {
        const thisUnrolled = this.expand(2);
        const otherUnrolled = other.expand(1);

        if (otherUnrolled.length > thisUnrolled.length) return false;

        for (let i = 0; i <= thisUnrolled.length - otherUnrolled.length; i++) {
            if (otherUnrolled.every((v, j) => v === thisUnrolled[i + j])) {
                return true;
            }
        }
        return false;
    }

    /**
     * Returns true if two paths are equivalent (each is a subpath of the other).
     */
    static areEquivalent(p1: LayoutNodePath, p2: LayoutNodePath): boolean {
        return p1.isSubpathOf(p2) && p2.isSubpathOf(p1);
    }
}


/**
 * Desugar any legacy directives still sitting on a {@link LayoutSpec} into the
 * `atomStyle` rules the layout actually reads.
 *
 * `parseLayoutSpec` already does this for YAML, leaving `icons`/`atomColors`
 * empty — but a `LayoutSpec` can also arrive as an object, via the
 * `setupLayout(spec: LayoutSpec, ...)` overload or `new LayoutInstance(spec)`
 * directly. Those paths never touch the parser, so a programmatically-built
 * spec would populate `directives.icons` and have it silently ignored: nothing
 * reads those arrays any more.
 *
 * `atomColors` carries the same latent hole — nothing has read it since the
 * legacy color desugar landed — so it is normalized here too. Note these arrays
 * hold *parsed* directives, not raw YAML: an {@link AtomColorDirective} spells
 * its color `color`, where the YAML form (which `atomColorToAtomStyleRule`
 * consumes) spells it `value`. Hence the mapping below rather than a reuse of
 * that parser-side helper. {@link AtomIconDirective} happens to match the YAML
 * shape exactly, so it can go through {@link iconToAtomStyleRule} directly.
 *
 * Idempotent by construction — a parsed spec has empty legacy arrays, so this
 * is a no-op and returns the spec untouched. Only a spec that actually carries
 * legacy directives is copied (shallowly, so the caller's object isn't
 * mutated). Selectorless/pathless entries drop, matching the parser exactly.
 */
export function normalizeLegacyDirectives(spec: LayoutSpec): LayoutSpec {
    const legacyIcons = spec.directives?.icons ?? [];
    const legacyAtomColors = spec.directives?.atomColors ?? [];
    if (legacyIcons.length === 0 && legacyAtomColors.length === 0) return spec;

    const fromAtomColors: (AtomStyleRule | null)[] = legacyAtomColors.map((d) => {
        if (!d.selector || d.selector.trim().length === 0) return null;
        if (typeof d.color !== 'string' || d.color.length === 0) return null;
        return { selector: d.selector, style: { borderStyle: { color: d.color } } };
    });

    const desugared: AtomStyleRule[] = [
        ...fromAtomColors,
        ...legacyIcons.map(iconToAtomStyleRule),
    ].filter((rule): rule is AtomStyleRule => rule !== null);

    return {
        ...spec,
        directives: {
            ...spec.directives,
            atomStyles: [...spec.directives.atomStyles, ...desugared],
            icons: [],
            atomColors: [],
        },
    };
}


export class LayoutInstance {


    readonly hideThisEdge = "_h_"
    static DISCONNECTED_PREFIX = "_d_"


    readonly DEFAULT_NODE_ICON_PATH: string = '';
    readonly DEFAULT_NODE_HEIGHT = 60;
    readonly DEFAULT_NODE_WIDTH = 100;

    private readonly _layoutSpec: LayoutSpec;

    public readonly minSepHeight = 15;
    public readonly minSepWidth = 15;

    private evaluator: IEvaluator;
    private instanceNum: number;

    private readonly alignmentEdgeStrategy: AlignmentEdgeStrategy;
    private readonly validatorStrategy: ConstraintValidatorStrategy;

    /**
     * Collector for selector errors encountered during layout generation.
     * Reset at the start of each generateLayout() call.
     */
    private selectorErrors: SelectorErrorDetail[] = [];

    /**
     * Collector for advisory warnings about individual constraints and
     * directives. Reset at the start of each generateLayout() call, so on an
     * animated trace each frame reports its own state rather than accumulating.
     */
    private warnings: LayoutWarning[] = [];

    /**
     * Dedup keys for {@link warnings}, scoped to one generateLayout() call.
     *
     * A single render evaluates the same selector many times over — isAttributeField
     * and isHiddenField re-evaluate theirs once per graph edge — and one selector
     * can name the same missing thing repeatedly. Keyed by spec item plus the
     * unresolved name, so two different constraints with the same typo are still
     * reported separately.
     */
    private warningKeys: Set<string> = new Set();

    /**
     * Map of hidden node IDs to the hideAtom selector that caused them to be hidden.
     * Populated by ensureNoExtraNodes() and used by constraint generation to detect conflicts.
     * Reset at the start of each generateLayout() call.
     */
    private hiddenNodeSelectors: Map<string, string> = new Map();

    /**
     * Map of hideAtom selector to the author-provided source of the directive
     * that used it, for citing the rule as its author wrote it in conflict
     * reports. Only selectors whose directive carried a `source` appear here.
     * Reset at the start of each generateLayout() call.
     */
    private hiddenSelectorSources: Map<string, ProvidedSource> = new Map();

    /**
     * Tracks constraints that were dropped because they reference hidden nodes.
     * Each entry maps a source constraint description (HTML) to a list of
     * pairwise constraint descriptions that were skipped.
     * Reset at the start of each generateLayout() call.
     */
    private hiddenNodeConflicts: Map<string, string[]> = new Map();

    /**
     * Set of hidden node IDs that are actually involved in conflicts (i.e., referenced
     * by at least one dropped constraint). Used to build hideAtom table entries without
     * parsing description strings.
     * Reset at the start of each generateLayout() call.
     */
    private conflictedHiddenNodes: Set<string> = new Set();

    /**
     * Per-end group attachments for inferredEdge `draw` edges, keyed by edge ID.
     * The graphlib edge anchors on member nodes; these stamps tell the renderer
     * which end(s) attach to a group hull instead of the anchor node.
     * Reset at the start of each generateLayout() call.
     */
    private inferredEdgeGroupStamps: Map<string, { sourceGroupId?: string; targetGroupId?: string }> = new Map();

    /**
     * Node-id fragments selected by non-negated cyclic constraints, recorded for
     * InstanceLayout.cyclicFragments (what the `cyclic()` spatial query reports).
     * Membership is settled by selection, so two-atom fragments are recorded even
     * though they need no disjunction to draw. Reset at the start of each
     * generateLayout() call.
     */
    private cyclicFragments: string[][] = [];

    /**
     * Atom IDs that a hideAtom directive would hide but which the counterfactual pass must
     * show, because a layout constraint references them. Honored by ensureNoExtraNodes.
     * Carried across the internal counterfactual pass, so it is reset only by the public
     * generateLayout() entry point, never by an individual pass.
     */
    private exemptFromHiding: Set<string> = new Set();

    /**
     * Atom IDs kept visible this pass despite matching a hideAtom directive, because a
     * constraint references them. These are the atoms the counterfactual diagram draws
     * with a dashed outline. Repopulated by ensureNoExtraNodes on each pass.
     */
    private reintroducedNodes: Set<string> = new Set();

    /**
     * Records a selector evaluation error for later reporting.
     * @param selector - The selector expression that failed
     * @param context - Description of where/why the selector was being evaluated
     * @param error - The original error that was caught
     */
    private recordSelectorError(selector: string, context: string, error: unknown): void {
        const errorMessage = error instanceof Error ? error.message : String(error);
        this.selectorErrors.push({
            selector,
            context,
            errorMessage
        });
    }

    /**
     * Records an advisory warning about one constraint or directive.
     *
     * Deduplicated within the render, because a selector is typically evaluated
     * many times per layout and would otherwise report the same missing name once
     * per evaluation.
     */
    private recordWarning(warning: LayoutWarning): void {
        const key = `${warning.code}|${warning.specType ?? ''}|${warning.specIndex ?? ''}|${warning.name ?? warning.selector ?? warning.message}`;
        if (this.warningKeys.has(key)) {
            return;
        }
        this.warningKeys.add(key);
        this.warnings.push(warning);
    }

    /**
     * Forwards the warnings `parseLayoutSpec` raised about the spec itself —
     * today, use of a deprecated form — onto this render's warning list.
     *
     * They are the same kind of thing the selector warnings are: the diagram
     * drew, nothing failed, but something in the spec deserves the author's
     * attention. The parse writes them to `console.warn` as well, which reaches
     * whoever opens devtools and nobody else; riding on the layout puts them on
     * the same badge as everything else, in front of the person editing the spec.
     *
     * Re-run per `generateLayout` call rather than once in the constructor, since
     * `this.warnings` is cleared each render (an animated trace reports its own
     * frame). The spec does not change between frames, so this replays the same
     * list — which is what should happen: the deprecation is still true.
     */
    private recordSpecParseWarnings(): void {
        for (const w of this._layoutSpec.warnings ?? []) {
            this.recordWarning({
                severity: 'warning',
                code: w.code,
                // The `[spytial]` prefix marks the source in a console shared with
                // the host page. On our own badge it is noise.
                message: w.message.replace(/^\[spytial\]\s*/, ''),
                // Where it came from: the spec text, not any one evaluation.
                context: 'spec',
                specType: w.specType,
                label: w.specType ?? 'spec'
            });
        }
    }

    /**
     * Drains any diagnostics the evaluator raised for `selector` and records one
     * warning per diagnostic, attributed to the spec item that owns the selector.
     *
     * Call this after every successful `evaluator.evaluate(...)`. An unresolved
     * name does not fail evaluation — it yields the empty set — so without this
     * the constraint silently does nothing and looks identical to one that
     * legitimately matched nothing.
     *
     * @param source - The owning constraint or directive. Constraints supply a
     *   `toHTML()` label; directives are plain objects and fall back to
     *   `type[index] · selector`.
     */
    private collectSelectorDiagnostics(
        selectorRes: IEvaluatorResult,
        selector: string,
        context: string,
        specType: string,
        specIndex: number,
        source?: { toHTML?: () => string }
    ): void {
        const diagnostics = selectorRes.getDiagnostics?.() ?? [];
        if (diagnostics.length === 0) {
            return;
        }
        const label = typeof source?.toHTML === 'function'
            ? stripHtml(source.toHTML())
            : `${specType}[${specIndex}] · ${selector}`;

        for (const d of diagnostics) {
            this.recordWarning({
                severity: 'warning',
                code: d.kind,
                message: warningMessage(d, specType),
                selector,
                context,
                specType,
                specIndex,
                label,
                name: d.name,
                suggestion: d.suggestion
            });
        }
    }

    /**
     * Checks whether the selector result has the expected arity.
     * Records a SelectorArityError and returns false if mismatched.
     * @param selectorRes - The evaluated selector result
     * @param selector - The selector expression (for error messages)
     * @param expectedArity - 'unary' or 'binary'
     * @param context - Description of where the selector is being used (e.g. 'orientation selector')
     * @returns true if arity matches (or result is empty), false if mismatched
     */
    private checkSelectorArity(
        selectorRes: IEvaluatorResult,
        selector: string,
        expectedArity: 'unary' | 'binary',
        context: string
    ): boolean {
        const arity = selectorRes.maxArity();
        if (arity === 0) {
            // No results — nothing to validate
            return true;
        }
        if (expectedArity === 'binary' && arity < 2) {
            this.recordSelectorError(
                selector,
                context,
                new SelectorArityError(selector, 'binary', 'unary')
            );
            return false;
        }
        if (expectedArity === 'unary' && arity > 1) {
            this.recordSelectorError(
                selector,
                context,
                new SelectorArityError(selector, 'unary', 'binary')
            );
            return false;
        }
        return true;
    }

    /**
     * Decides whether a just-evaluated selector result is usable, recording
     * anything noteworthy on the way. Returns false when the owning constraint or
     * directive should be skipped — every *other* one still applies.
     *
     * This exists because `evaluator.evaluate()` does not throw on a bad selector:
     * simple-graph-query returns an error *result*, and the throw only happens
     * later, inside `selectedTwoples()` / `selectedAtoms()` / `selectedTuplesAll()`.
     * At several call sites those extractors sat outside the try/catch, so a single
     * malformed selector escaped the local handler and killed the whole layout.
     * `checkSelectorArity` did not catch it either — `maxArity()` reports 0 for an
     * error result, and arity 0 reads as "no results, nothing to validate".
     *
     * @param expectedArity - `'any'` skips the arity check, for sites that legitimately
     *   accept both (group-by-selector) or validate arity themselves.
     */
    private acceptSelectorResult(
        selectorRes: IEvaluatorResult,
        selector: string,
        context: string,
        expectedArity: 'unary' | 'binary' | 'any',
        specType: string,
        specIndex: number,
        source?: { toHTML?: () => string }
    ): boolean {
        // Collect diagnostics before the error check. A selector that warns about an
        // unresolved name and *then* fails for an unrelated reason should still
        // surface the warning — that is when it is most worth having.
        this.collectSelectorDiagnostics(selectorRes, selector, context, specType, specIndex, source);

        // Guarded rather than called outright: `isError` is part of IEvaluatorResult,
        // but hand-rolled and third-party evaluators in the wild implement only the
        // extractors they need. Skipping the check for those leaves them exactly as
        // they behaved before, instead of failing on a missing method.
        if (typeof selectorRes.isError === 'function' && selectorRes.isError()) {
            this.recordSelectorError(selector, context, new Error(selectorRes.prettyPrint()));
            return false;
        }
        if (expectedArity !== 'any' && !this.checkSelectorArity(selectorRes, selector, expectedArity, context)) {
            return false;
        }
        return true;
    }

    /**
     * Checks if a node is hidden by a hideAtom directive.
     * @param nodeId - The node ID to check
     * @returns true if the node was hidden by a hideAtom selector
     */
    private isNodeHiddenByDirective(nodeId: string): boolean {
        return this.hiddenNodeSelectors.has(nodeId);
    }

    /**
     * Records a conflict where a constraint references a hidden node — an unsatisfiable
     * combination: the atom cannot be both hidden and placed. The tuple is skipped for
     * this pass; generateLayout() then builds the counterfactual diagram by re-running
     * with the hidden atoms shown, and reports the conflict as a layout error.
     * @param sourceConstraint - The source constraint that references the hidden node
     * @param pairwiseDescription - A human-readable description of the conflicting pairwise constraint
     * @param sourceNodeId - The source node of the conflicting relationship
     * @param targetNodeId - The target node of the conflicting relationship
     * @param sourceHidden - Whether the source node is hidden by a directive
     * @param targetHidden - Whether the target node is hidden by a directive
     */
    private recordHiddenNodeConflict(
        sourceConstraint: ConstraintSource,
        pairwiseDescription: string,
        sourceNodeId: string,
        targetNodeId: string,
        sourceHidden: boolean,
        targetHidden: boolean
    ): void {
        const sourceHTML = sourceConstraint.toHTML();
        if (!this.hiddenNodeConflicts.has(sourceHTML)) {
            this.hiddenNodeConflicts.set(sourceHTML, []);
        }
        this.hiddenNodeConflicts.get(sourceHTML)!.push(pairwiseDescription);

        // Track which hidden nodes are involved in conflicts, for the hideAtom table
        // entries and for the counterfactual pass that shows them.
        if (sourceHidden) {
            this.conflictedHiddenNodes.add(sourceNodeId);
        }
        if (targetHidden) {
            this.conflictedHiddenNodes.add(targetNodeId);
        }
    }

    /**
     * Records a conflict where a group contains a hideAtom-hidden atom — unsatisfiable
     * for the same reason as the pairwise case: the atom cannot be both hidden and drawn
     * inside the group. generateLayout() shows the atom in the counterfactual and reports
     * the conflict as a layout error. (Group keys are exempt: a binary group's key is not
     * inside the hull, and hiding keys while keeping the group is a sanctioned pattern.)
     * @param group - The group that contains the hidden atom
     * @param hiddenNodeId - The hidden member atom
     */
    private recordHiddenGroupMemberConflict(group: LayoutGroup, hiddenNodeId: string): void {
        const sourceHTML = group.sourceConstraint?.toHTML() ?? `group <code>${group.name}</code>`;
        const description = `${hiddenNodeId} is in group ${group.name}`;
        if (!this.hiddenNodeConflicts.has(sourceHTML)) {
            this.hiddenNodeConflicts.set(sourceHTML, []);
        }
        this.hiddenNodeConflicts.get(sourceHTML)!.push(description);
        this.conflictedHiddenNodes.add(hiddenNodeId);
    }


    /**
     * Constructs a new `LayoutInstance` object.
     *
     * @param layoutSpec - The layout specification that defines constraints, directives, and other layout-related configurations.
     * @param evaluator - An evaluator instance used to evaluate selectors and constraints within the layout specification.
     * @param instNum - The instance number (default is 0), used to differentiate between multiple instances of the same layout.
     * @param addAlignmentEdges - Deprecated. Use alignmentEdgeStrategy instead. A boolean flag indicating whether alignment edges should be added (default is `true`, equivalent to 'connected' strategy).
     * @param alignmentEdgeStrategy - Strategy for adding alignment edges (default is `AlignmentEdgeStrategy.CONNECTED`). Takes precedence over addAlignmentEdges if provided.
     * @param validatorStrategy - Which constraint validator to use. `QUALITATIVE` is the only strategy and the default; the parameter is kept so existing positional callers still compile.
     *
     * The `LayoutInstance` class is responsible for generating a layout for a given data instance based on the provided layout specification.
     * It applies constraints and directives to produce a structured layout that can be rendered using a graph visualization library.
     */
    constructor(
        layoutSpec: LayoutSpec,
        evaluator: IEvaluator,
        instNum: number = 0,
        addAlignmentEdges: boolean = true,
        alignmentEdgeStrategy?: AlignmentEdgeStrategy,
        validatorStrategy?: ConstraintValidatorStrategy
    ) {
        this.instanceNum = instNum;
        this.evaluator = evaluator;
        this._layoutSpec = normalizeLegacyDirectives(layoutSpec);

        // Handle backward compatibility: if alignmentEdgeStrategy is provided, use it
        // Otherwise, convert boolean addAlignmentEdges to strategy
        if (alignmentEdgeStrategy !== undefined) {
            this.alignmentEdgeStrategy = alignmentEdgeStrategy;
        } else {
            this.alignmentEdgeStrategy = addAlignmentEdges
                ? AlignmentEdgeStrategy.CONNECTED
                : AlignmentEdgeStrategy.NEVER;
        }

        this.validatorStrategy = validatorStrategy ?? ConstraintValidatorStrategy.QUALITATIVE;
    }

    get hideDisconnected(): boolean {
        return this._layoutSpec.directives.hideDisconnected || false;
    }

    get hideDisconnectedBuiltIns(): boolean {
        return this._layoutSpec.directives.hideDisconnectedBuiltIns || false;
    }



    isAttributeField(fieldId: string, sourceAtom?: string, targetAtom?: string): boolean {
        const matchingDirectives = this._layoutSpec.directives.attributes.filter((ad) => ad.field === fieldId);
        
        if (matchingDirectives.length === 0) {
            return false;
        }
        
        // If no atoms provided, use legacy behavior for directives without selector/filter
        if (!sourceAtom || !targetAtom) {
            return matchingDirectives.some(ad => !ad.selector && !ad.filter);
        }
        
        // Check selector-based and filter-based directives
        for (const directive of matchingDirectives) {
            // Check if selector matches (or no selector means match all sources)
            let selectorMatches = true;
            if (directive.selector) {
                try {
                    const selectorResult = this.evaluator.evaluate(directive.selector, { instanceIndex: this.instanceNum });
                    const selectedAtoms = selectorResult.selectedAtoms();
                    selectorMatches = selectedAtoms.includes(sourceAtom);
                } catch (error) {
                    this.recordSelectorError(directive.selector, 'attribute selector', error);
                    selectorMatches = false;
                }
            }
            
            if (!selectorMatches) {
                continue;
            }
            
            // Check if filter matches (or no filter means match all tuples)
            let filterMatches = true;
            if (directive.filter) {
                try {
                    const filterResult = this.evaluator.evaluate(directive.filter, { instanceIndex: this.instanceNum });
                    const selectedTuples = filterResult.selectedTwoples();
                    // Check if the (source, target) pair is in the filtered set
                    filterMatches = selectedTuples.some(
                        tuple => tuple[0] === sourceAtom && tuple[1] === targetAtom
                    );
                } catch (error) {
                    this.recordSelectorError(directive.filter, 'attribute filter', error);
                    filterMatches = false;
                }
            }
            
            if (selectorMatches && filterMatches) {
                return true;
            }
        }
        
        return false;
    }

    isHiddenField(fieldId: string, sourceAtom?: string, targetAtom?: string): boolean {
        // First check hiddenFields directives
        const matchingDirectives = this._layoutSpec.directives.hiddenFields.filter((hd) => hd.field === fieldId);
        
        if (matchingDirectives.length > 0) {
            // If no atoms provided, use legacy behavior for directives without selector/filter
            if (!sourceAtom || !targetAtom) {
                if (matchingDirectives.some(hd => !hd.selector && !hd.filter)) {
                    return true;
                }
            } else {
                // Check selector-based and filter-based directives
                for (const directive of matchingDirectives) {
                    // Check if selector matches (or no selector means match all sources)
                    let selectorMatches = true;
                    if (directive.selector) {
                        try {
                            const selectorResult = this.evaluator.evaluate(directive.selector, { instanceIndex: this.instanceNum });
                            const selectedAtoms = selectorResult.selectedAtoms();
                            selectorMatches = selectedAtoms.includes(sourceAtom);
                        } catch (error) {
                            this.recordSelectorError(directive.selector, 'hidden field selector', error);
                            selectorMatches = false;
                        }
                    }
                    
                    if (!selectorMatches) {
                        continue;
                    }
                    
                    // Check if filter matches (or no filter means match all tuples)
                    let filterMatches = true;
                    if (directive.filter) {
                        try {
                            const filterResult = this.evaluator.evaluate(directive.filter, { instanceIndex: this.instanceNum });
                            const selectedTuples = filterResult.selectedTwoples();
                            // Check if the (source, target) pair is in the filtered set
                            filterMatches = selectedTuples.some(
                                tuple => tuple[0] === sourceAtom && tuple[1] === targetAtom
                            );
                        } catch (error) {
                            this.recordSelectorError(directive.filter, 'hidden field filter', error);
                            filterMatches = false;
                        }
                    }
                    
                    if (selectorMatches && filterMatches) {
                        return true;
                    }
                }
            }
        }
        
        // Also check edgeStyle rules (including desugared edgeColor) with hidden: true
        if (sourceAtom && targetAtom) {
            const styled = this.resolveEdgeStyleForEdge(fieldId, sourceAtom, targetAtom);
            if (styled.hidden === true) {
                return true;
            }
        }
        
        return false;
    }


    /**
     * Generates groups based on the specified graph.
     * @param g - The graph, which will be modified to remove the edges that are used to generate groups.
     * @param a - The Data Instance.
     * @returns A record of groups.
     */
    private normalizeComparableGroupToken(token: string): string {
        const trimmed = token.trim();
        if (
            (trimmed.startsWith('"') && trimmed.endsWith('"'))
            || (trimmed.startsWith("'") && trimmed.endsWith("'"))
        ) {
            return trimmed.slice(1, -1);
        }
        return trimmed;
    }

    private buildGroupKeyDisplay(nodeId: string, nodeLabel?: string): string {
        const normalizedId = this.normalizeComparableGroupToken(nodeId);
        const rawLabel = (nodeLabel || '').trim();

        if (!rawLabel) {
            return normalizedId || nodeId;
        }

        const normalizedLabel = this.normalizeComparableGroupToken(rawLabel);

        // Avoid redundant keys like x1:"x1" when they represent the same value.
        if (normalizedLabel === normalizedId) {
            return normalizedId || normalizedLabel || nodeId;
        }

        return `${rawLabel}:${nodeId}`;
    }

    private generateGroups(g: Graph, _a: IDataInstance): LayoutGroup[] {

        //let groupingConstraints : GroupingConstraint[] = this._layoutSpec.constraints.grouping;


        const groupBySelectorConstraints: GroupBySelector[] = this._layoutSpec.constraints.grouping.byselector;

        if (!groupBySelectorConstraints) {
            return [];
        }

        let groups: LayoutGroup[] = [];

        // First we go through the group by selector constraints.
        for (const [specIndex, gc] of groupBySelectorConstraints.entries()) {

            let selector = gc.selector;
            let selectorRes;
            try {
                selectorRes = this.evaluator.evaluate(selector, { instanceIndex: this.instanceNum });
            } catch (error) {
                this.recordSelectorError(selector, 'groupBySelector selector', error);
                continue; // Skip this group constraint
            }

            // Arity is 'any' here: groups intentionally accept both unary and binary
            // selectors, and the branch below picks the path from maxArity().
            if (!this.acceptSelectorResult(selectorRes, selector, 'groupBySelector selector', 'any', 'group', specIndex, gc)) {
                continue; // Skip this group only — every other constraint still applies
            }

            // Now, we should support both unary and binary selectors.

            // Use maxArity to decide which path to take, avoiding SelectorArityError
            // since groups intentionally support both unary and binary selectors.
            let isBinary = selectorRes.maxArity() > 1;
            
            if (isBinary) {
                let selectedTwoples: string[][] = selectorRes.selectedTwoples();

                function constructGroupEdgeID(edgelabel: string, src : string, tgt: string): string {
                    return `_g_0_1_` + edgelabel + `:` + src + `->` + tgt;
                }

                // The first element of each tuple is the key (i.e. groupOn)
                // The second element is the element to add to the group (i.e. addToGroup)

                // The name of the group is the relation name ':' the key node.

                for (var t of selectedTwoples) {

                    // Here, it should be the ID and the label of the node.



                    let groupOn = t[0];
                    let addToGroup = t[1];

                    const keyNodeLabel = g.node(groupOn)?.label;
                    const groupOnLabel = this.buildGroupKeyDisplay(groupOn, keyNodeLabel);
                    let groupName = `${gc.name}[${groupOnLabel}]`;

                    // Check if the group already exists
                    let existingGroup: LayoutGroup | undefined = groups.find((group) => group.name === groupName);

                    if (existingGroup) {
                        existingGroup.nodeIds.push(addToGroup);
                    }
                    else {
                        let newGroup: LayoutGroup =
                        {
                            name: groupName,
                            nodeIds: [addToGroup],
                            keyNodeId: groupOn,
                            showLabel: !gc.negated,
                            labelTextStyle: gc.labelTextStyle,
                            sourceConstraint: gc,
                            negated: gc.negated,
                            keyed: true
                        };
                        groups.push(newGroup);

                        // NOW if the GroupBySelector requests an edge, draw it between the key
                        // node (groupOn) and the group, in the requested direction:
                        //   'togroup'   → key → group   (edge source = key)
                        //   'fromgroup' → group → key   (edge source = member)
                        // The member end is later snapped to the group hull by the renderer,
                        // which identifies the key end via the edge's stamped keyNodeId.
                        if (gc.addEdge !== 'none') {
                            const [edgeSrc, edgeTgt] = gc.addEdge === 'fromgroup'
                                ? [addToGroup, groupOn]
                                : [groupOn, addToGroup];
                            const edgeId = constructGroupEdgeID(groupName, edgeSrc, edgeTgt);
                            g.setEdge(edgeSrc, edgeTgt, groupName, edgeId);
                        }

                    }
                }




            }
            else {
                let selectedElements: string[] = selectorRes.selectedAtoms();

                // Nothing to do if there are no selected elements, or 
                // if things are typed weirdly.
                if (selectedElements.length === 0) {
                    continue;
                }

                let keyNode = selectedElements[0]; // TODO: WAIT, THERE IS NO KEY NODE

                // Question: Does **just** having LayoutGroup work? Like what does a keyNode even mean?
                let newGroup: LayoutGroup = {
                    name: gc.name,
                    nodeIds: selectedElements,
                    keyNodeId: keyNode, //// TODO: I think introducing this random keynode could be a problem. Not sure why or when though.
                    showLabel: !gc.negated,
                    labelTextStyle: gc.labelTextStyle,
                    sourceConstraint: gc,
                    negated: gc.negated,
                    keyed: false
                };
                groups.push(newGroup);
            }
        }


        return groups;
    }

    /**
     * Generates groups based on the specified graph.
     * @param g - The graph, which will be modified to remove the edges that are used to determine attributes.
     * @returns A record of attributes
     */
    private generateAttributesAndRemoveEdges(g: Graph): {
        attributes: Record<string, Record<string, string[]>>;
        textStyles: Record<string, Record<string, TextStyle>>;
    } {
        // Node : [] of attributes
        let attributes: Record<string, Record<string, string[]>> = {};
        // Node : attrKey -> shared TextStyle (size + color), parallel to `attributes`.
        let textStyles: Record<string, Record<string, TextStyle>> = {};

        let graphEdges = [...g.edges()];
        // Go through all edge labels in the graph

        graphEdges.forEach((edge) => {
            const edgeId = edge.name;
            const relName = this.getRelationName(g, edge);
            const sourceAtom = edge.v;
            const targetAtom = edge.w;
            const isAttributeRel = this.isAttributeField(relName, sourceAtom, targetAtom);
            const isHiddenRel = this.isHiddenField(relName, sourceAtom, targetAtom);

            if (isHiddenRel && isAttributeRel) {
                throw new Error(`${relName} cannot be both an attribute and a hidden field.`);
            }

            if (isHiddenRel) {
                // If the field is a hidden field, we should remove the edge from the graph.
                g.removeEdge(edge.v, edge.w, edgeId);
                return;
            }

            if (isAttributeRel) {

                // If the field is an attribute field, we should add the attribute to the source node's
                // attributes field.

                const attributeKey = this.getEdgeLabel(g, edge);
                let source = edge.v;
                let target = edge.w;

                // Really, we should be pushing the target node's LABEL.
                let targetLabel = g.node(target)?.label || target; // Use the node's label or the node ID if no label exists.

                let nodeAttributes = attributes[source] || {};

                if (!nodeAttributes[attributeKey]) {
                    nodeAttributes[attributeKey] = [];
                    attributes[source] = nodeAttributes;
                }
                nodeAttributes[attributeKey].push(targetLabel);

                // Record the text style (size + color) from the matching attribute directive.
                const style = this.getAttributeTextStyle(relName, sourceAtom, targetAtom);
                if (style) {
                    const nodeStyles = textStyles[source] || (textStyles[source] = {});
                    nodeStyles[attributeKey] = style;
                }

                // Now remove the edge from the graph
                g.removeEdge(edge.v, edge.w, edgeId);
            }
        });

        return { attributes, textStyles };
    }

    /**
     * Text style (the shared `textStyle` block: size + color) for an attribute
     * field, taken from the first matching `attribute` directive (mirroring
     * {@link isAttributeField}'s selector/filter matching). Returns `undefined`
     * when no matching directive sets a `textStyle`, so the line falls back to
     * the default secondary size and inherited label color.
     */
    private getAttributeTextStyle(fieldId: string, sourceAtom: string, targetAtom: string): TextStyle | undefined {
        const matchingDirectives = this._layoutSpec.directives.attributes.filter((ad) => ad.field === fieldId);

        for (const directive of matchingDirectives) {
            let selectorMatches = true;
            if (directive.selector) {
                try {
                    const selectorResult = this.evaluator.evaluate(directive.selector, { instanceIndex: this.instanceNum });
                    selectorMatches = selectorResult.selectedAtoms().includes(sourceAtom);
                } catch {
                    selectorMatches = false;
                }
            }
            if (!selectorMatches) continue;

            let filterMatches = true;
            if (directive.filter) {
                try {
                    const filterResult = this.evaluator.evaluate(directive.filter, { instanceIndex: this.instanceNum });
                    filterMatches = filterResult.selectedTwoples().some(
                        tuple => tuple[0] === sourceAtom && tuple[1] === targetAtom
                    );
                } catch {
                    filterMatches = false;
                }
            }
            if (!filterMatches) continue;

            return directive.textStyle;
        }

        return undefined;
    }

    /**
     * Generates tag-based attributes for nodes based on TagDirectives.
     * Unlike attributes which come from edges, tags are computed from n-ary selectors
     * and added to nodes selected by the toTag selector.
     * 
     * For n-ary selector results (e.g., x1->y1->z1, x2->y2->z2), the format is:
     *   name[x1][y1]: z1
     *   name[x2][y2]: z2
     * 
     * For unary selector results (single atom), it's just:
     *   name: value
     * 
     * @param g - The graph to get node information from.
     * @param existingAttributes - Existing attributes to merge tags into.
     * @returns Updated attributes record with tags merged in.
     */
    private generateTagsForNodes(
        g: Graph,
        existingAttributes: Record<string, Record<string, string[]>>,
        existingTextStyles: Record<string, Record<string, TextStyle>>
    ): {
        attributes: Record<string, Record<string, string[]>>;
        textStyles: Record<string, Record<string, TextStyle>>;
    } {
        const tagDirectives = this._layoutSpec.directives.tags;

        if (!tagDirectives || tagDirectives.length === 0) {
            return { attributes: existingAttributes, textStyles: existingTextStyles };
        }

        const attributes = { ...existingAttributes };
        const textStyles = { ...existingTextStyles };
        const graphNodes = new Set(g.nodes());

        // Stamp the tag's TextStyle (size + color) onto its attribute key (parallel to `attributes`).
        const recordStyle = (atomId: string, attrKey: string, style: TextStyle | undefined) => {
            if (!style) return;
            const nodeStyles = textStyles[atomId] || (textStyles[atomId] = {});
            nodeStyles[attrKey] = style;
        };

        for (const directive of tagDirectives) {
            const tagStyle: TextStyle | undefined = directive.textStyle;
            try {
                // First, evaluate the toTag selector to get which nodes receive this tag
                const toTagResult = this.evaluator.evaluate(directive.toTag, { instanceIndex: this.instanceNum });
                const selectedAtoms = toTagResult.selectedAtoms();
                
                // Then, evaluate the value selector. Its result becomes the tag
                // values, one line per tuple, at whatever arity it comes back.
                //
                // `selectedTuplesAll` drops single-atom tuples in the data
                // evaluators, so a unary value selector used to reach the loop
                // below with nothing in hand and tag no node at all — even
                // though that loop has always had a unary branch. Feed those
                // atoms through as 1-tuples, the same shape and the same
                // maxArity() test addDrawInferredEdges uses, so a tag means the
                // same thing whichever evaluator produced the result.
                const valueResult = this.evaluator.evaluate(directive.value, { instanceIndex: this.instanceNum });
                const allTuples: string[][] = valueResult.maxArity() > 1
                    ? valueResult.selectedTuplesAll()
                    : valueResult.selectedAtoms().map((atom: string) => [atom]);

                // Index by first atom, which is what each tagged node looks up.
                // Scanning the whole list per node was affordable while a unary
                // value produced no tuples at all; a membership tag returns one
                // per atom, so the same scan would be |toTag| x |value|.
                const tuplesByFirstAtom = new Map<string, string[][]>();
                for (const tuple of allTuples) {
                    const bucket = tuplesByFirstAtom.get(tuple[0]);
                    if (bucket) bucket.push(tuple);
                    else tuplesByFirstAtom.set(tuple[0], [tuple]);
                }

                // For each node that should receive this tag
                for (const atomId of selectedAtoms) {
                    // Only add tags to nodes that exist in the graph
                    if (!graphNodes.has(atomId)) {
                        continue;
                    }
                    
                    // Ensure the node has an attributes object
                    if (!attributes[atomId]) {
                        attributes[atomId] = {};
                    }
                    
                    // Find tuples where the first element matches this atom
                    const matchingTuples = tuplesByFirstAtom.get(atomId) ?? [];

                    if (matchingTuples.length === 0) {
                        // No matching tuples for this atom, skip
                        continue;
                    }
                    
                    for (const tuple of matchingTuples) {
                        if (tuple.length === 1) {
                            // Unary tuple: just name: value (the atom itself)
                            const attrKey = directive.name;
                            if (!attributes[atomId][attrKey]) {
                                attributes[atomId][attrKey] = [];
                            }
                            // For unary, the value is the atom label itself
                            const nodeLabel = g.node(atomId)?.label || atomId;
                            attributes[atomId][attrKey].push(String(nodeLabel));
                            recordStyle(atomId, attrKey, tagStyle);
                        } else if (tuple.length === 2) {
                            // Binary tuple: name: lastValue
                            const attrKey = directive.name;
                            if (!attributes[atomId][attrKey]) {
                                attributes[atomId][attrKey] = [];
                            }
                            const lastValue = tuple[tuple.length - 1];
                            // Get the label for the last element if it's a node
                            const valueLabel = graphNodes.has(String(lastValue))
                                ? (g.node(String(lastValue))?.label || String(lastValue))
                                : String(lastValue);
                            attributes[atomId][attrKey].push(valueLabel);
                            recordStyle(atomId, attrKey, tagStyle);
                        } else {
                            // N-ary tuple (n > 2): name[mid1][mid2]...: lastValue
                            // The key includes all middle elements
                            const middleElements = tuple.slice(1, -1);
                            const lastValue = tuple[tuple.length - 1];
                            
                            // Format middle elements with brackets
                            const middlePart = middleElements
                                .map(el => {
                                    const elStr = String(el);
                                    // Use node label if it's a graph node
                                    const label = graphNodes.has(elStr)
                                        ? (g.node(elStr)?.label || elStr)
                                        : elStr;
                                    return `[${label}]`;
                                })
                                .join('');
                            
                            const attrKey = `${directive.name}${middlePart}`;
                            if (!attributes[atomId][attrKey]) {
                                attributes[atomId][attrKey] = [];
                            }
                            
                            // Get the label for the last element if it's a node
                            const valueLabel = graphNodes.has(String(lastValue))
                                ? (g.node(String(lastValue))?.label || String(lastValue))
                                : String(lastValue);
                            attributes[atomId][attrKey].push(valueLabel);
                            recordStyle(atomId, attrKey, tagStyle);
                        }
                    }
                }
            } catch (error) {
                this.recordSelectorError(
                    `name: ${directive.name}, toTag: ${directive.toTag}, value: ${directive.value}`,
                    'tag directive',
                    error
                );
            }
        }

        return { attributes, textStyles };
    }

    /**
    * Modifies the graph to remove extraneous nodes (ex. those to be hidden)
    * Also populates this.hiddenNodeSelectors so constraint generation can detect conflicts,
    * and records hide-vs-group conflicts against the already-generated groups (groups are
    * built before hiding, so membership is checked here at hide time).
    * @param g - The graph, which will be modified to remove extraneous nodes.
    * @param groups - The groups generated for this pass, checked for hidden members/keys.
    */
    private ensureNoExtraNodes(g: Graph, a: IDataInstance, groups: LayoutGroup[]) {

        let nodes = [...g.nodes()];

        // Pre-evaluate all hideAtom directives once (not per node)
        const hiddenAtomDirectives = this._layoutSpec.directives.hiddenAtoms;
        const evaluatedHideDirectives: { selector: string; hiddenSet: Set<string> }[] = [];
        for (const [specIndex, directive] of hiddenAtomDirectives.entries()) {
            try {
                const selectorResult = this.evaluator.evaluate(directive.selector, { instanceIndex: this.instanceNum });
                if (!this.acceptSelectorResult(selectorResult, directive.selector, 'hideAtom selector', 'unary', 'hideAtom', specIndex)) {
                    continue; // Skip this directive only (recorded once)
                }
                const selectedAtoms = selectorResult.selectedAtoms();
                evaluatedHideDirectives.push({
                    selector: directive.selector,
                    hiddenSet: new Set(selectedAtoms),
                });
                // Remember the author-provided source per selector, so a
                // conflict report can cite the rule as its author wrote it.
                if (directive.source && !this.hiddenSelectorSources.has(directive.selector)) {
                    this.hiddenSelectorSources.set(directive.selector, directive.source);
                }
            } catch (error) {
                this.recordSelectorError(directive.selector, 'hideAtom selector', error);
            }
        }

        nodes.forEach((node) => {


            // Check if builtin
            try {
                const type = a.getAtomType(node);
                const isAtomBuiltin = type?.isBuiltin || false;

                let inEdges = g.inEdges(node) || [];
                let outEdges = g.outEdges(node) || [];
                const isDisconnected = inEdges.length === 0 && outEdges.length === 0;


                // Legacy hiding logic for backwards compatibility
                const hideLegacy = isDisconnected && ((this.hideDisconnectedBuiltIns && isAtomBuiltin) || this.hideDisconnected);

                // New selector-based hiding logic
                let hideBySelector = false;
                let hidingSelector: string | undefined;
                for (const evaluated of evaluatedHideDirectives) {
                    if (evaluated.hiddenSet.has(node)) {
                        hideBySelector = true;
                        hidingSelector = evaluated.selector;
                        break;
                    }
                }

                const hideNode = hideLegacy || hideBySelector;

                // A hideAtom-selected node that a layout constraint references is kept for
                // the counterfactual pass — the hide and the constraint cannot both hold, so
                // generateLayout() reports an error and draws the atom anyway (see there).
                if (hideBySelector && this.exemptFromHiding.has(node)) {
                    this.reintroducedNodes.add(node);
                    return; // keep the node; do not record it as hidden
                }

                if (hideNode) {
                    g.removeNode(node);
                    // Track selector-hidden nodes so constraint generation can report conflicts
                    if (hideBySelector && hidingSelector) {
                        this.hiddenNodeSelectors.set(node, hidingSelector);
                        // A group that contains this atom cannot be drawn without it —
                        // the hide and the group cannot both be satisfied. Only members
                        // count: a binary group's key is not inside the hull (hiding keys
                        // while keeping the group is a sanctioned pattern; see the
                        // inferred-edge draw tests), and negated groups assert
                        // non-containment, so a hidden member does not contradict them.
                        for (const group of groups) {
                            if (group.negated) continue;
                            if (group.nodeIds.includes(node)) {
                                this.recordHiddenGroupMemberConflict(group, node);
                            }
                        }
                    }
                }

            } catch (error) {
                console.error("Failed to identify node type. Defaulting to showing node.", error);
            }
        });
    }


    /**
     * The atoms hideAtom constraints removed this pass, for InstanceLayout.hiddenAtoms.
     * Sorted so the layout is deterministic; undefined when nothing was hidden by
     * selector, matching how other optional layout fields are carried.
     */
    private collectHiddenAtoms(): string[] | undefined {
        if (this.hiddenNodeSelectors.size === 0) return undefined;
        return [...this.hiddenNodeSelectors.keys()].sort();
    }

    /** The cyclic fragments recorded this pass, for InstanceLayout.cyclicFragments. */
    private collectCyclicFragments(): string[][] | undefined {
        if (this.cyclicFragments.length === 0) return undefined;
        return this.cyclicFragments.map(fragment => [...fragment]);
    }

    private getMostSpecificType(node: string, a: IDataInstance): string {
        let allTypes = this.getNodeTypes(node, a);
        let mostSpecificType = allTypes[0];
        return mostSpecificType;
    }

    private getNodeTypes(node: string, a: IDataInstance): string[] {
        let type = a.getAtomType(node);
        let allTypes = type?.types || [];
        allTypes = allTypes.concat(UNIVERSAL_TYPE);
        return allTypes;
    }


    public getRelationName(g: Graph, edge: Edge): string {
        let relNameRaw = this.getEdgeLabel(g, edge);

        try {

            let relName = relNameRaw.split("[")[0];
            return relName;
        }
        catch {
            console.warn(`Failed to parse relation name from edge label: ${relNameRaw}. Defaulting to empty string.`);
            return relNameRaw;
        }
    }

    private getEdgeLabel(g: Graph, edge: Edge): string {
        return g.edge(edge.v, edge.w, edge.name);
    }

    /**
     * Generates the layout for the given data instance.
     * The engine lays out exactly the instance it is handed. To diagram a
     * projected view, project in the host and pass the resulting instance —
     * spytial-core dropped its own projection helpers in 6.0.0.
     *
     * When a hideAtom directive hides an atom that a layout constraint references, the
     * spec is unsatisfiable: the atom cannot be both hidden and placed. The returned
     * error is a HiddenNodeConflictError describing the conflict, and the returned layout
     * is a counterfactual diagram in which the conflicting atoms are shown despite the
     * hide (marked on `layout.reintroducedNodes`; the renderer draws them with a dashed
     * outline) so the conflicting relationships are visible. This is implemented by
     * re-running generation with the conflicting atoms exempted from hiding, repeated
     * until a pass reports no new conflicts — showing an atom can surface conflicts an
     * earlier, aborted pass never reached (e.g. one that stopped at a missing-node error
     * before processing later constraints). Typically one re-run.
     *
     * @param a - The data instance to generate the layout for.
     * @returns An object containing the layout, constraint error (if any), and any selector errors encountered.
     * @throws {ConstraintError} If the layout cannot be generated due to unsatisfiable constraints and error isn't caught to be surfaced to the user.
     */
    public generateLayout(
        a: IDataInstance
    ): CounterfactualLayoutResult {
        // Fresh exemption set per public call; it is the only state that must survive the
        // internal counterfactual passes below.
        this.exemptFromHiding = new Set();

        let result = this.generateLayoutPass(a);
        if (this.conflictedHiddenNodes.size === 0) {
            return result;
        }

        // The spec is unsatisfiable. Accumulate the conflict explanation across passes —
        // each pass's tracking maps are reset by the next — and re-run with the
        // conflicting atoms shown until a pass converges. Every round strictly grows
        // exemptFromHiding (an exempted atom is never hidden, so never re-conflicted),
        // which bounds the loop by the number of hidden atoms; the hard cap is a
        // defensive backstop only.
        const allConflicts = new Map<string, string[]>();
        const allConflictedIds = new Set<string>();
        const allHiddenSelectors = new Map<string, string>();
        const absorbPassConflicts = () => {
            for (const [source, descriptions] of this.hiddenNodeConflicts) {
                const merged = allConflicts.get(source) ?? [];
                for (const d of descriptions) {
                    if (!merged.includes(d)) {
                        merged.push(d);
                    }
                }
                allConflicts.set(source, merged);
            }
            this.conflictedHiddenNodes.forEach(id => allConflictedIds.add(id));
            for (const [node, selector] of this.hiddenNodeSelectors) {
                if (!allHiddenSelectors.has(node)) {
                    allHiddenSelectors.set(node, selector);
                }
            }
        };

        const MAX_PASSES = 10;
        let passes = 1;
        while (this.conflictedHiddenNodes.size > 0 && passes < MAX_PASSES) {
            absorbPassConflicts();
            this.conflictedHiddenNodes.forEach(id => this.exemptFromHiding.add(id));
            result = this.generateLayoutPass(a);
            passes++;
        }
        if (this.conflictedHiddenNodes.size > 0) {
            // Unreachable if the loop invariant holds; keep the explanation whole anyway.
            absorbPassConflicts();
            console.warn(`Hidden-node conflict resolution did not converge after ${passes} passes.`);
        }

        // Mark the shown-despite-hide atoms on the layout, and attach the merged conflict
        // error. A different conflict surfaced by the final pass (e.g. positional) takes
        // precedence, but a per-pass hidden-node error is replaced by the merged one so
        // the explanation covers every pass.
        const reintroduced = result.layout.nodes.filter(n => this.reintroducedNodes.has(n.id));
        if (reintroduced.length > 0) {
            result.layout.reintroducedNodes = reintroduced;
        }
        if (result.error === null || isHiddenNodeConflictError(result.error)) {
            const conflictInfo = this.buildHiddenNodeConflictInfo(allConflicts, allConflictedIds, allHiddenSelectors);
            conflictInfo.reintroducedNodeIds = reintroduced.map(n => n.id);
            result = { ...result, error: conflictInfo };
        }

        return result;
    }

    /**
     * Runs a single layout-generation pass. Honors `this.exemptFromHiding` so the
     * counterfactual pass can show previously-hidden atoms. Not for direct use — call
     * generateLayout().
     */
    private generateLayoutPass(
        a: IDataInstance
    ): CounterfactualLayoutResult {
        // Reset selector errors at the start of each layout generation
        this.selectorErrors = [];
        this.warnings = [];
        this.warningKeys.clear();
        // Spec-level warnings (deprecated forms) first, so they read before the
        // per-item selector warnings this render is about to collect.
        this.recordSpecParseWarnings();
        // Reset hidden-node tracking at the start of each layout generation
        // (exemptFromHiding is intentionally preserved across passes)
        this.hiddenNodeSelectors = new Map();
        this.hiddenSelectorSources = new Map();
        this.hiddenNodeConflicts = new Map();
        this.conflictedHiddenNodes = new Set();
        this.reintroducedNodes = new Set();
        this.inferredEdgeGroupStamps = new Map();
        this.cyclicFragments = [];

        let ai = a;

        // Do not apply hideDisconnectedBuiltIns here — inferred edges may connect built-in atoms.
        // We apply built-in hiding afterwards in `ensureNoExtraNodes` so inferred edges can reconnect them.
        let g: Graph = ai.generateGraph(this.hideDisconnected, false);

        let { attributes, textStyles: attributeTextStyles } = this.generateAttributesAndRemoveEdges(g);

        // Generate and merge tags into attributes (carrying per-key text styles alongside)
        ({ attributes, textStyles: attributeTextStyles } = this.generateTagsForNodes(g, attributes, attributeTextStyles));

        // This is where we add the inferred edges to the graph.
        this.addinferredEdges(g);


        /// Groups have to happen here ///
        let groups = this.generateGroups(g, a);

        // Draw-qualified inferred edges resolve their endpoints against the
        // generated groups, so they join the graph only now (before hiding,
        // like plain inferred edges — so they can reconnect built-in atoms).
        this.addDrawInferredEdges(g, groups);

        this.ensureNoExtraNodes(g, a, groups);

        // Resolve atomStyle once: it feeds the border color (via the color map)
        // and the node's fill / border-width / label / icon styling below.
        let atomStyleMap = this.getAtomStyleMap(g, ai);
        let { colorMap: nodeColorMap, explicitlyColored } = this.getNodeColorMap(g, ai, atomStyleMap);

        // Compute the display strings once — single source of truth for "what's
        // drawn inside the box" so the box sizer (getNodeSizeMap) and the
        // LayoutNode mapping below see the same content.
        let contentByNode = this.getNodeDisplayContent(g, ai, attributes, attributeTextStyles);
        let nodeSizeMap = this.getNodeSizeMap(g, contentByNode);

        let dcN = this.getDisconnectedNodes(g);



        /// NOW, we should get the nodes back with their IDs.



        let layoutNodes: LayoutNode[] = g.nodes().map((nodeId) => {

            let nodeMetadata = g.node(nodeId);
            // If the node has a label, we can use it.
            // Otherwise, we can use the nodeId as the label.
            let label = nodeMetadata?.label || nodeId;
            let color = nodeColorMap[nodeId] || "black";
            // Algorithm-assigned (default palette) unless an explicit color directive hit it.
            let colorSource = explicitlyColored.has(nodeId)
                ? ColorSource.Directive
                : ColorSource.DefaultPalette;
            let { height, width } = nodeSizeMap[nodeId];

            const mostSpecificType = this.getMostSpecificType(nodeId, a);
            const allTypes = this.getNodeTypes(nodeId, a);

            let nodeGroups = groups
                .filter((group) => group.nodeIds.includes(nodeId))
                .map((group) => group.name);
            let nodeAttributes = attributes[nodeId] || {};
            let nodeAttributeTextStyles = attributeTextStyles[nodeId] || {};

            // Get labels from the data instance (e.g., Skolems in Alloy)
            let nodeLabels: Record<string, string[]> | undefined = undefined;
            const atoms = ai.getAtoms();
            const atom = atoms.find(atom => atom.id === nodeId);
            if (atom?.labels) {
                nodeLabels = atom.labels;
            }

            // Fill / border-width / label / icon styling from the resolved
            // atomStyle (border *color* already flowed through nodeColorMap →
            // color above).
            const atomStyle = atomStyleMap[nodeId];
            const iconStyle = atomStyle?.iconStyle;
            // An atom shows its label unless a rule says otherwise; the legacy
            // `icon` directive desugars an explicit false for its icon-only default.
            const showLabels = atomStyle?.showLabel ?? true;
            // Placement only matters once there is a path to draw; `full` is the
            // default so a bare `iconStyle: { path }` reads as "this icon IS the atom".
            const iconPlacement = iconStyle?.placement ?? 'full';

            return {
                id: nodeId,
                label: label,
                name: label,
                color: color,
                colorSource: colorSource,
                fillColor: atomStyle?.fillStyle?.color,
                borderWidth: atomStyle?.borderStyle?.width,
                textStyle: atomStyle?.textStyle,
                groups: nodeGroups,
                attributes: nodeAttributes,
                attributeTextStyles: nodeAttributeTextStyles,
                labels: nodeLabels,
                icon: iconStyle?.path ?? this.DEFAULT_NODE_ICON_PATH,
                iconPlacement: iconPlacement,
                iconOpacity: iconStyle?.opacity,
                height: height,
                width: width,
                mostSpecificType: mostSpecificType,
                types: allTypes,
                showLabels: showLabels,
                disconnected: dcN.includes(nodeId)
            };
        });

        ///////////// CONSTRAINTS ////////////


        let constraints: LayoutConstraint[] = [];
        let orientationConstraintCount = 0;
        let alignConstraintCount = 0;
        let layoutEdges: LayoutEdge[] = [];
        let cyclicDisjunctions: DisjunctiveConstraint[] = [];

        // Collect disjunctive constraints from negated alignment and cyclic
        let allDisjunctions: DisjunctiveConstraint[] = [];

        try {
            const orientationResult = this.applyRelativeOrientationConstraints(layoutNodes, g);
            constraints = orientationResult.conjunctive;
            allDisjunctions.push(...orientationResult.disjunctive);
            orientationConstraintCount = constraints.length;

            const alignResult = this.applyAlignConstraints(layoutNodes, g);
            constraints = constraints.concat(alignResult.conjunctive);
            allDisjunctions.push(...alignResult.disjunctive);
            alignConstraintCount = alignResult.conjunctive.length;

            console.log(`Generated ${orientationConstraintCount} orientation constraints and ${alignConstraintCount} alignment constraints (deduped + transitive reduction applied)`);

            // Prune redundant alignment edges after all have been added
            this.pruneRedundantAlignmentEdges(g);

            // Constraints NOW holds the conjuctive CORE of layout constraints.
            constraints = removeDuplicateConstraints(constraints);

            // Build cyclic constraint disjunctions (must happen before buildLayoutEdges
            // so that alignment edges added for cyclic pairs are included in the edge list)
            cyclicDisjunctions = this.buildCyclicDisjunctions(layoutNodes, g);
            allDisjunctions.push(...cyclicDisjunctions);

            layoutEdges = this.buildLayoutEdges(g, layoutNodes, groups);
        } catch (error) {
            if (isMissingNodeConstraintError(error)) {
                return this.handleMissingNodeConstraintError(
                    error,
                    {
                        layoutNodes,
                        graph: g,
                        groups,
                        disconnectedNodes: dcN,
                        constraints
                    }
                );
            }
            throw error;
        }

        // Create layout with conjunctive constraints and disjunctive constraints
        let layout: InstanceLayout = {
            nodes: layoutNodes,
            edges: layoutEdges,
            constraints: constraints,
            groups: groups,
            disjunctiveConstraints: allDisjunctions.length > 0 ? allDisjunctions : undefined,
            hiddenAtoms: this.collectHiddenAtoms(),
            cyclicFragments: this.collectCyclicFragments()
        };

        // Constraint tuples referencing hideAtom-hidden atoms were skipped during
        // generation, so the spec is unsatisfiable. Return this pass's layout with the
        // conflict as the error; generateLayout() builds the real counterfactual by
        // re-running with those atoms shown. Negated groups are filtered out like the
        // normal path does — they never draw a rectangle.
        if (this.hiddenNodeConflicts.size > 0) {
            const counterfactualLayout: InstanceLayout = {
                ...layout,
                edges: this.filterHiddenEdges(layout.edges),
                groups: layout.groups.filter(gr => !gr.negated),
                warnings: this.warnings
            };
            return {
                layout: counterfactualLayout,
                error: this.buildHiddenNodeConflictInfo(),
                selectorErrors: this.selectorErrors,
                warnings: this.warnings
            };
        }

        // Validate all constraints (conjunctive + disjunctive) in one pass
        const validator: IConstraintValidator = new QualitativeConstraintValidator(layout);
        const constraintError = validator.validateConstraints();

        if (constraintError) {
            if (isPositionalConstraintError(constraintError)) {
                return this.handlePositionalConstraintError(constraintError, layout);
            }

            if (isGroupOverlapError(constraintError)) {
                return this.handleGroupOverlapError(constraintError, layout);
            }

            throw constraintError;
        }

        // Update constraints with those added by the validator
        // (includes chosen alternatives from disjunctions and implicit alignment constraints)
        constraints = layout.constraints;

        // Filter out all edges that are hidden
        layoutEdges = this.filterHiddenEdges(layoutEdges);

        // Update the layout with final groups
        // Filter out negated groups — they don't produce visual rectangles;
        // their disjunctions were already added to the solver by the validators.
        layout.nodes = layoutNodes;
        layout.edges = layoutEdges;
        layout.constraints = constraints;
        layout.groups = groups.filter(g => !g.negated);

        // Return layout with selectorErrors and warnings (if any) - neither blocks
        // the layout, but callers should check and display them to the user
        const qualitativeValidator = validator instanceof QualitativeConstraintValidator ? validator : undefined;
        layout.warnings = this.warnings;
        return { layout, error: null, selectorErrors: this.selectorErrors, warnings: this.warnings, validator: qualitativeValidator };
    }

    /**
     * Handles errors where a constraint references a node missing from the graph
     * (e.g., hidden by a hideAtom directive). Returns a counterfactual layout
     * built from the constraints that could be applied.
     */
    private handleMissingNodeConstraintError(
        error: MissingNodeConstraintError,
        context: {
            layoutNodes: LayoutNode[],
            graph: Graph,
            groups: LayoutGroup[],
            disconnectedNodes: string[],
            constraints: LayoutConstraint[],
        }
    ): CounterfactualLayoutResult {
        const layoutEdges = this.filterHiddenEdges(
            this.buildLayoutEdges(context.graph, context.layoutNodes, context.groups)
        );
        const layoutGroups = context.groups;

        const counterfactualLayout: InstanceLayout = {
            nodes: context.layoutNodes,
            edges: layoutEdges,
            constraints: context.constraints,
            groups: layoutGroups,
            disjunctiveConstraints: [],
            warnings: this.warnings,
            hiddenAtoms: this.collectHiddenAtoms(),
            cyclicFragments: this.collectCyclicFragments()
        };

        return {
            layout: counterfactualLayout,
            error,
            selectorErrors: this.selectorErrors,
            warnings: this.warnings
        };
    }

    /**
     * Builds the structured explanation for a hideAtom-vs-constraint conflict (IIS-like
     * table + summary message). The hide and the constraint are mutually unsatisfiable —
     * the table lists both sides, exactly like other IIS conflict reports.
     *
     * Defaults to the current pass's tracking maps (they are reset by the next pass, so
     * call before re-running); the orchestrator passes maps accumulated across passes
     * instead, so the explanation covers conflicts an aborted earlier pass never reached.
     */
    private buildHiddenNodeConflictInfo(
        conflicts: Map<string, string[]> = this.hiddenNodeConflicts,
        conflictedNodeIds: Set<string> = this.conflictedHiddenNodes,
        hiddenSelectors: Map<string, string> = this.hiddenNodeSelectors
    ): HiddenNodeConflictError {
        // Build the IIS-like error messages map:
        //   Source Constraint HTML → list of conflicting pairwise constraint descriptions
        // Also add a synthetic entry for each hideAtom directive involved
        const sourceConstraintHTMLToLayoutConstraintsHTML = new Map<string, string[]>();

        // Collect all unique hideAtom selectors involved
        const involvedHideSelectors = new Set<string>();

        for (const [sourceHTML, descriptions] of conflicts.entries()) {
            sourceConstraintHTMLToLayoutConstraintsHTML.set(sourceHTML, descriptions);
        }

        // Derive involved hideAtom selectors from the conflicted node ids
        for (const nodeId of conflictedNodeIds) {
            const selector = hiddenSelectors.get(nodeId);
            if (selector) {
                involvedHideSelectors.add(selector);
            }
        }

        // Add hideAtom directives as source constraints in the table. Only the atoms
        // actually involved in a conflict are listed (they are tracked in
        // conflictedNodeIds and were recorded in hiddenSelectors when hidden).
        const conflictedIds = [...conflictedNodeIds];
        for (const hideSelector of involvedHideSelectors) {
            // Prefer the rule as its author wrote it, when the embedding
            // provided it — same fallback the constraint toHTML() path uses.
            const provided = this.hiddenSelectorSources.get(hideSelector);
            const hideHTML = provided
                ? providedSourceHTML(provided)
                : `hideAtom with selector <code>${hideSelector}</code>`;
            const hiddenNodeDescriptions: string[] = [];
            for (const nodeId of conflictedIds) {
                if (hiddenSelectors.get(nodeId) === hideSelector) {
                    hiddenNodeDescriptions.push(`${nodeId} is hidden`);
                }
            }
            if (hiddenNodeDescriptions.length > 0) {
                sourceConstraintHTMLToLayoutConstraintsHTML.set(hideHTML, hiddenNodeDescriptions);
            }
        }

        // Build a summary message
        const firstConflictSourceHTML = [...conflicts.keys()][0];
        const firstConflictDescription = conflicts.get(firstConflictSourceHTML)?.[0] || '';

        const errorMessages: ErrorMessages = {
            conflictingConstraint: firstConflictDescription,
            conflictingSourceConstraint: firstConflictSourceHTML,
            minimalConflictingConstraints: sourceConstraintHTMLToLayoutConstraintsHTML
        };

        const message = `Layout constraints reference atoms [${conflictedIds.join(', ')}] that a hideAtom directive hides. The hide and the constraints cannot both be satisfied. The diagram shows the layout as if those atoms were not hidden; they are drawn with a dashed outline.`;

        return {
            name: 'HiddenNodeConflictError',
            type: 'hidden-node-conflict',
            message,
            hiddenNodes: hiddenSelectors,
            droppedConstraints: conflicts,
            errorMessages
        };
    }

    /**
     * Handles positional constraint conflicts (unsatisfiable ordering/alignment).
     * Returns a counterfactual layout enforcing the maximal feasible subset of
     * constraints, with conflicting constraints annotated for visual highlighting.
     */
    private handlePositionalConstraintError(
        error: PositionalConstraintError,
        layout: InstanceLayout
    ): CounterfactualLayoutResult {
        const minimalConflictingSet = error.minimalConflictingSet;

        // The qualitative validator enforces the MFS on layout.constraints
        // before returning the error. Use the layout as-is in that case.
        // `maximalFeasibleSubset` is optional on the error type, so a validator
        // that does not compute one falls back to removing the IIS.
        const constraints = error.maximalFeasibleSubset
            ? layout.constraints
            : layout.constraints.filter(c =>
                ![...minimalConflictingSet.values()].flat().includes(c)
            );

        const counterfactualLayout: InstanceLayout = {
            nodes: layout.nodes,
            edges: layout.edges,
            constraints,
            groups: layout.groups,
            conflictingConstraints: [...minimalConflictingSet.values()].flat(),
            warnings: this.warnings,
            hiddenAtoms: layout.hiddenAtoms,
            cyclicFragments: layout.cyclicFragments
        };
        return {
            layout: counterfactualLayout,
            error: error,
            selectorErrors: this.selectorErrors,
            warnings: this.warnings
        };
    }

    /**
     * Handles group overlap errors where nodes belong to multiple non-overlapping
     * groups. Returns a counterfactual layout scoped to the overlapping groups
     * and their nodes, with overlapping nodes annotated for visual highlighting.
     */
    private handleGroupOverlapError(
        error: GroupOverlapError,
        layout: InstanceLayout
    ): CounterfactualLayoutResult {
        // Get overlapping groups
        const overlappingGroupNames = error.overlappingNodes.map(node => node.groups).flat();
        const overlappingGroups = layout.groups.filter(group =>
            overlappingGroupNames.includes(group.name)
        );

        // Get relevant nodes, which are nodes in the overlapping groups
        const relevantNodeIds = overlappingGroups.flatMap(group => group.nodeIds)
        const relevantNodes = layout.nodes.filter(node => relevantNodeIds.includes(node.id));

        // Only edges containing overlapping nodes
        const edgesWithRelevantNodes = layout.edges.filter(edge =>
            relevantNodes.some(node => edge.source.id === node.id) && relevantNodes.some(node => edge.target.id === node.id)
        );
        
        const counterfactualLayout: InstanceLayout = {
            nodes: relevantNodes,
            edges: edgesWithRelevantNodes,
            constraints: layout.constraints,
            groups: overlappingGroups,
            overlappingNodes: error.overlappingNodes,
            warnings: this.warnings,
            hiddenAtoms: layout.hiddenAtoms,
            cyclicFragments: layout.cyclicFragments
        }
        return { 
            layout: counterfactualLayout, 
            error: error,
            selectorErrors: this.selectorErrors,
            warnings: this.warnings
        };
    }

    /**
     * Applies the cyclic orientation constraints to the layout nodes.
     * @param layoutNodes - The layout nodes to which the constraints will be applied.
     * @throws {ConstraintError} If the layout cannot be satisfied with the cyclic constraints.
     * @returns An array of layout constraints.
     */
    /**
     * Builds disjunctive constraints for cyclic orientation constraints.
     * Each cyclic fragment generates a disjunction with N alternatives (perturbations),
     * where N is the number of nodes in the fragment.
     * 
     * @param layoutNodes - The layout nodes to which the constraints will be applied.
     * @returns Array of DisjunctiveConstraint instances, one per cyclic fragment.
     */
    private buildCyclicDisjunctions(layoutNodes: LayoutNode[], g: Graph): DisjunctiveConstraint[] {
        const cyclicConstraints = this._layoutSpec.constraints.orientation.cyclic;
        const disjunctions: DisjunctiveConstraint[] = [];

        // For each cyclic constraint, extract fragments
        for (const [specIndex, c] of cyclicConstraints.entries()) {
            let selectorRes;
            try {
                selectorRes = this.evaluator.evaluate(c.selector, { instanceIndex: this.instanceNum });
            } catch (error) {
                this.recordSelectorError(c.selector, 'cyclic orientation selector', error);
                continue; // Skip this cyclic constraint
            }
            if (!this.acceptSelectorResult(selectorRes, c.selector, 'cyclic orientation selector', 'binary', 'cyclic', specIndex, c)) {
                continue; // Skip this constraint only — every other one still applies
            }
            let selectedTuples: string[][] = selectorRes.selectedTwoples();
            let nextNodeMap: Map<LayoutNode, LayoutNode[]> = new Map<LayoutNode, LayoutNode[]>();
            
            // Build nextNodeMap from selected tuples
            selectedTuples.forEach((tuple) => {
                let sourceNodeId = tuple[0];
                let targetNodeId = tuple[1];

                // Skip tuples referencing hidden nodes; record the conflict for error reporting
                // (the atom cannot be both hidden and placed on the cycle)
                const sourceHidden = this.isNodeHiddenByDirective(sourceNodeId);
                const targetHidden = this.isNodeHiddenByDirective(targetNodeId);
                if (sourceHidden || targetHidden) {
                    const description = `${sourceNodeId} is followed by ${targetNodeId} in a ${c.direction} cycle`;
                    this.recordHiddenNodeConflict(c, description, sourceNodeId, targetNodeId, sourceHidden, targetHidden);
                    return; // Skip this tuple
                }

                let srcN = layoutNodes.find((node) => node.id === sourceNodeId);
                let tgtN = layoutNodes.find((node) => node.id === targetNodeId);

                // Skip if either node is not found (hidden for a non-directive reason,
                // e.g. legacy disconnected-node hiding)
                if (!srcN || !tgtN) {
                    return;
                }

                // Add an alignment edge so WebCola applies spring forces between
                // cyclic-constrained nodes, which is needed for positional constraints
                // to take effect when there is no existing graph edge between them.
                if (this.shouldAddAlignmentEdge(g, sourceNodeId, targetNodeId)) {
                    const alignmentEdgeLabel = `_alignment_${sourceNodeId}_${targetNodeId}_`;
                    g.setEdge(sourceNodeId, targetNodeId, alignmentEdgeLabel, alignmentEdgeLabel);
                }

                if (nextNodeMap.has(srcN)) {
                    nextNodeMap.get(srcN)!.push(tgtN);
                }
                else {
                    nextNodeMap.set(srcN, [tgtN]);
                }
            });

            let relatedNodeFragments = this.getFragmentsToConstrain(nextNodeMap);
            let relatedNodeIds = relatedNodeFragments.map((p) => p.Path.map((node) => node.id));

            // Apply counterclockwise reversal if needed
            if (c.direction === "counterclockwise") {
                relatedNodeIds = relatedNodeIds.map((fragment) => fragment.reverse());
            }

            // For each fragment, create a disjunction with N perturbations
            relatedNodeIds.forEach((fragment) => {
                const fragmentLength = fragment.length;

                // Membership is settled by selection, not by the solver: a pair
                // the selector closed into a fragment is still "in a cycle" even
                // though drawing it needs no disjunction. Singletons cycle with
                // nobody and are not recorded. Negated cyclic asserts the
                // absence of a cycle, so it contributes no members either.
                if (!c.negated && fragmentLength >= 2) {
                    this.cyclicFragments.push([...fragment]);
                }

                // Fragments with 2 or fewer nodes don't need disjunctions
                if (fragmentLength <= 2) {
                    return;
                }

                // Generate all perturbations (rotations) of this fragment
                const alternatives: LayoutConstraint[][] = [];
                for (let perturbation = 0; perturbation < fragmentLength; perturbation++) {
                    const constraintsForPerturbation = this.getCyclicConstraintForFragment(
                        fragment,
                        layoutNodes,
                        perturbation,
                        c
                    );
                    alternatives.push(constraintsForPerturbation);
                }

                if (c.negated) {
                    // NOT(cyclic) via De Morgan: NOT(alt1 ∨ alt2 ∨ ...) = ¬alt1 ∧ ¬alt2 ∧ ...
                    // Build the positive disjunction, then negate it.
                    const positiveDisjunction = new DisjunctiveConstraint(c, alternatives);
                    const negatedDisjunctions = negateDisjunction(positiveDisjunction, c);
                    disjunctions.push(...negatedDisjunctions);
                } else {
                    // Create the disjunctive constraint
                    const disjunction = new DisjunctiveConstraint(c, alternatives);
                    disjunctions.push(disjunction);
                }
            });
        }

        return disjunctions;
    }

    private getCyclicConstraintForFragment(fragment: string[],
        layoutNodes: LayoutNode[],
        perturbationIdx: number,
        c: RelativeOrientationConstraint | CyclicOrientationConstraint | ImplicitConstraint): LayoutConstraint[] {
        const minRadius = 100;


        if (fragment.length <= 2) {
            return []; // No constraints needed for a two-node fragment.
        }

        const angleStep = (2 * Math.PI) / fragment.length;

        let fragmentNodePositions: Record<string, { x: number, y: number }> = {};

        for (var i = 0; i < fragment.length; i++) {
            let theta = (i + perturbationIdx) * angleStep;
            let x = minRadius * Math.cos(theta);
            let y = minRadius * Math.sin(theta);
            fragmentNodePositions[fragment[i]] = { x: x, y: y };
        }

        let fragmentConstraintsForCurrentOffset: LayoutConstraint[] = [];
        for (var k = 0; k < fragment.length; k++) {
            for (var j = 0; j < fragment.length; j++) {
                if (k !== j) {
                    let node1 = fragment[k];
                    let node2 = fragment[j];
                    let node1_pos = fragmentNodePositions[node1];
                    let node2_pos = fragmentNodePositions[node2];

                    if (node1_pos.x > node2_pos.x) {
                        fragmentConstraintsForCurrentOffset.push(this.leftConstraint(node2, node1, this.minSepWidth, layoutNodes, c));
                    }
                    else if (node1_pos.x < node2_pos.x) {
                        fragmentConstraintsForCurrentOffset.push(this.leftConstraint(node1, node2, this.minSepWidth, layoutNodes, c));
                    }
                    else {
                        // If they are on the same x-axis, we need to ensure that they are not on top of each other
                        fragmentConstraintsForCurrentOffset.push(this.ensureSameXConstraint(node1, node2, layoutNodes, c));
                    }

                    if (node1_pos.y > node2_pos.y) {
                        fragmentConstraintsForCurrentOffset.push(this.topConstraint(node2, node1, this.minSepHeight, layoutNodes, c));
                    }
                    else if (node1_pos.y < node2_pos.y) {
                        fragmentConstraintsForCurrentOffset.push(this.topConstraint(node1, node2, this.minSepHeight, layoutNodes, c));
                    }
                    else {
                        // If they are on the same y-axis, we need to ensure that they are not on top of each other
                        fragmentConstraintsForCurrentOffset.push(this.ensureSameYConstraint(node1, node2, layoutNodes, c));
                    }
                }
            }

        }

        return fragmentConstraintsForCurrentOffset;
    }




    private getAllPaths(nextNodeMap: Map<LayoutNode, LayoutNode[]>): LayoutNodePath[] {

        const allPaths: LayoutNodePath[] = [];

        const visited = new Set<LayoutNode>(); // To track visited nodes in the current path

        function dfs(currentNode: LayoutNode, path: LayoutNode[]): void {
            // Add the current node to the path
            path.push(currentNode);

            // If the current node has no outgoing edges, add the path to allPaths
            const neighbors = nextNodeMap.get(currentNode);
            if (!nextNodeMap.has(currentNode) || !neighbors || neighbors.length === 0) {

                let lnp = new LayoutNodePath(path, undefined);
                allPaths.push(lnp);
            } else {
                // Recursively visit all neighbors
                for (const neighbor of neighbors) {
                    if (!path.includes(neighbor)) {
                        // Continue DFS if the neighbor is not already in the path
                        dfs(neighbor, [...path]); // Pass a copy of the path to avoid mutation
                    } else {

                        let lnp = new LayoutNodePath(path, neighbor);
                        // If the neighbor is already in the path, we have a cycle
                        allPaths.push(lnp);
                    }
                }
            }
        }

        // Start DFS from each node in the map
        for (const startNode of nextNodeMap.keys()) {
            if (!visited.has(startNode)) { // Since we have already documented the paths from this node.
                dfs(startNode, []);
            }
        }

        return allPaths;
    }

    private getFragmentsToConstrain(nextNodeMap: Map<LayoutNode, LayoutNode[]>): LayoutNodePath[] {
        // Ensure allPaths are instances of the LayoutNodePath class
        const allPaths: LayoutNodePath[] = this.getAllPaths(nextNodeMap);

        // Step 1: Remove equivalent paths (keep only one representative per equivalence class)
        const nonEquivalentPaths: LayoutNodePath[] = allPaths.filter((p, i) => {
            return !allPaths.some((p2, j) => j < i && LayoutNodePath.areEquivalent(p, p2));
        });

        // Step 2: Remove paths that are strict subpaths of others
        const nonSubsumedPaths: LayoutNodePath[] = nonEquivalentPaths.filter((p, i) => {
            return !nonEquivalentPaths.some((p2, j) => i !== j && p2.isSubpathOf(p));
        });

        return nonSubsumedPaths;
    }



    /**
     * Applies the relative orientation constraints to the layout nodes.
     * Includes transitive reduction: if a < b and b < c exist, don't add a < c.
     * @param layoutNodes - The layout nodes to which the constraints will be applied.
     * @returns Conjunctive constraints and disjunctive constraints (from negation).
     */
    applyRelativeOrientationConstraints(layoutNodes: LayoutNode[], g: Graph): {
        conjunctive: LayoutConstraint[];
        disjunctive: DisjunctiveConstraint[];
    } {

        let constraints: LayoutConstraint[] = [];
        let disjunctions: DisjunctiveConstraint[] = [];
        let relativeOrientationConstraints = this._layoutSpec.constraints.orientation.relative;
        
        // Track generated constraints to avoid duplicates
        // Key format: "type:node1:node2:distance" (e.g., "left:a:b:15")
        const generatedConstraints = new Set<string>();
        
        // Track transitive relationships for left/right and top/bottom
        // Maps: node → set of nodes it's left of / above
        const leftOfGraph = new Map<string, Set<string>>();
        const aboveGraph = new Map<string, Set<string>>();

        relativeOrientationConstraints.forEach((c: RelativeOrientationConstraint, specIndex: number) => {

            let directions = c.directions;
            let selector = c.selector;

            let selectorRes;
            try {
                selectorRes = this.evaluator.evaluate(selector, { instanceIndex: this.instanceNum });
            } catch (error) {
                this.recordSelectorError(selector, 'orientation selector', error);
                return; // Skip this orientation constraint
            }
            if (!this.acceptSelectorResult(selectorRes, selector, 'orientation selector', 'binary', 'orientation', specIndex, c)) {
                return; // Skip this constraint only — every other one still applies
            }
            let selectedTuples: string[][] = selectorRes.selectedTwoples();

            // For each tuple, we need to apply the constraints
            selectedTuples.forEach((tuple) => {
                let sourceNodeId = tuple[0];
                let targetNodeId = tuple[1];

                // Skip tuples referencing hidden nodes; record the conflict for error reporting
                const sourceHidden = this.isNodeHiddenByDirective(sourceNodeId);
                const targetHidden = this.isNodeHiddenByDirective(targetNodeId);
                if (sourceHidden || targetHidden) {
                    // Record one description per direction in natural language
                    for (const dir of directions) {
                        const description = describeDroppedOrientationConstraint(sourceNodeId, targetNodeId, dir);
                        this.recordHiddenNodeConflict(c, description, sourceNodeId, targetNodeId, sourceHidden, targetHidden);
                    }
                    return; // Skip this tuple
                }

                // For negated multi-direction constraints, De Morgan applies:
                // NOT(dir1 AND dir2) = NOT(dir1) OR NOT(dir2)
                // We collect all negated alternatives across directions for this tuple
                // and wrap them in a single DisjunctiveConstraint.
                if (c.negated) {
                    // Add alignment edge
                    if (this.shouldAddAlignmentEdge(g, sourceNodeId, targetNodeId)) {
                        const alignmentEdgeLabel = `_alignment_${sourceNodeId}_${targetNodeId}_`;
                        g.setEdge(sourceNodeId, targetNodeId, alignmentEdgeLabel, alignmentEdgeLabel);
                    }

                    if (directions.length === 1) {
                        // Single direction: negation is a single conjunctive constraint
                        // (or a disjunction for directly* variants)
                        const negated = this.negateOrientationDirection(
                            directions[0], sourceNodeId, targetNodeId, layoutNodes, c
                        );
                        if (negated.length === 1) {
                            // Single flipped constraint — add conjunctively
                            constraints.push(negated[0][0]);
                        } else {
                            // Disjunction (directly* variants produce NOT(order) OR NOT(align))
                            disjunctions.push(new DisjunctiveConstraint(c, negated));
                        }
                    } else {
                        // Multiple directions: NOT(d1 AND d2 AND ...) = NOT(d1) OR NOT(d2) OR ...
                        // Each negated direction may itself produce multiple alternatives
                        // (e.g., directly* variants), so we flatten them all into one disjunction.
                        const allAlternatives: LayoutConstraint[][] = [];
                        for (const direction of directions) {
                            const negated = this.negateOrientationDirection(
                                direction, sourceNodeId, targetNodeId, layoutNodes, c
                            );
                            allAlternatives.push(...negated);
                        }
                        disjunctions.push(new DisjunctiveConstraint(c, allAlternatives));
                    }
                    return; // Skip positive constraint logic for this tuple
                }

                directions.forEach((direction) => {
                    // Add alignment edge for ALL orientation constraints if enabled AND edge doesn't already exist in the graph
                    if (this.shouldAddAlignmentEdge(g, sourceNodeId, targetNodeId)) {
                        const alignmentEdgeLabel = `_alignment_${sourceNodeId}_${targetNodeId}_`;
                        g.setEdge(sourceNodeId, targetNodeId, alignmentEdgeLabel, alignmentEdgeLabel);
                    }

                    if (direction == "left") {
                        const key = `left:${targetNodeId}:${sourceNodeId}:${this.minSepWidth}`;
                        // Check if transitively implied: is there a path targetNodeId -> ... -> sourceNodeId?
                        if (!generatedConstraints.has(key) && !this.hasTransitivePath(leftOfGraph, targetNodeId, sourceNodeId)) {
                            generatedConstraints.add(key);
                            this.addToTransitiveGraph(leftOfGraph, targetNodeId, sourceNodeId);
                            constraints.push(this.leftConstraint(targetNodeId, sourceNodeId, this.minSepWidth, layoutNodes, c));
                        }
                    }
                    else if (direction == "above") {
                        const key = `top:${targetNodeId}:${sourceNodeId}:${this.minSepHeight}`;
                        if (!generatedConstraints.has(key) && !this.hasTransitivePath(aboveGraph, targetNodeId, sourceNodeId)) {
                            generatedConstraints.add(key);
                            this.addToTransitiveGraph(aboveGraph, targetNodeId, sourceNodeId);
                            constraints.push(this.topConstraint(targetNodeId, sourceNodeId, this.minSepHeight, layoutNodes, c));
                        }
                    }
                    else if (direction == "right") {
                        const key = `left:${sourceNodeId}:${targetNodeId}:${this.minSepWidth}`;
                        if (!generatedConstraints.has(key) && !this.hasTransitivePath(leftOfGraph, sourceNodeId, targetNodeId)) {
                            generatedConstraints.add(key);
                            this.addToTransitiveGraph(leftOfGraph, sourceNodeId, targetNodeId);
                            constraints.push(this.leftConstraint(sourceNodeId, targetNodeId, this.minSepWidth, layoutNodes, c));
                        }
                    }
                    else if (direction == "below") {
                        const key = `top:${sourceNodeId}:${targetNodeId}:${this.minSepHeight}`;
                        if (!generatedConstraints.has(key) && !this.hasTransitivePath(aboveGraph, sourceNodeId, targetNodeId)) {
                            generatedConstraints.add(key);
                            this.addToTransitiveGraph(aboveGraph, sourceNodeId, targetNodeId);
                            constraints.push(this.topConstraint(sourceNodeId, targetNodeId, this.minSepHeight, layoutNodes, c));
                        }
                    }
                    else if (direction == "directlyLeft") {
                        const leftKey = `left:${targetNodeId}:${sourceNodeId}:${this.minSepWidth}`;
                        const alignKey = `align-y:${targetNodeId}:${sourceNodeId}`;
                        if (!generatedConstraints.has(leftKey) && !this.hasTransitivePath(leftOfGraph, targetNodeId, sourceNodeId)) {
                            generatedConstraints.add(leftKey);
                            this.addToTransitiveGraph(leftOfGraph, targetNodeId, sourceNodeId);
                            constraints.push(this.leftConstraint(targetNodeId, sourceNodeId, this.minSepWidth, layoutNodes, c));
                        }
                        if (!generatedConstraints.has(alignKey)) {
                            generatedConstraints.add(alignKey);
                            constraints.push(this.ensureSameYConstraint(targetNodeId, sourceNodeId, layoutNodes, c));
                        }
                    }
                    else if (direction == "directlyAbove") {
                        const topKey = `top:${targetNodeId}:${sourceNodeId}:${this.minSepHeight}`;
                        const alignKey = `align-x:${targetNodeId}:${sourceNodeId}`;
                        if (!generatedConstraints.has(topKey) && !this.hasTransitivePath(aboveGraph, targetNodeId, sourceNodeId)) {
                            generatedConstraints.add(topKey);
                            this.addToTransitiveGraph(aboveGraph, targetNodeId, sourceNodeId);
                            constraints.push(this.topConstraint(targetNodeId, sourceNodeId, this.minSepHeight, layoutNodes, c));
                        }
                        if (!generatedConstraints.has(alignKey)) {
                            generatedConstraints.add(alignKey);
                            constraints.push(this.ensureSameXConstraint(targetNodeId, sourceNodeId, layoutNodes, c));
                        }
                    }
                    else if (direction == "directlyRight") {
                        const leftKey = `left:${sourceNodeId}:${targetNodeId}:${this.minSepWidth}`;
                        const alignKey = `align-y:${targetNodeId}:${sourceNodeId}`;
                        if (!generatedConstraints.has(leftKey) && !this.hasTransitivePath(leftOfGraph, sourceNodeId, targetNodeId)) {
                            generatedConstraints.add(leftKey);
                            this.addToTransitiveGraph(leftOfGraph, sourceNodeId, targetNodeId);
                            constraints.push(this.leftConstraint(sourceNodeId, targetNodeId, this.minSepWidth, layoutNodes, c));
                        }
                        if (!generatedConstraints.has(alignKey)) {
                            generatedConstraints.add(alignKey);
                            constraints.push(this.ensureSameYConstraint(targetNodeId, sourceNodeId, layoutNodes, c));
                        }
                    }
                    else if (direction == "directlyBelow") {
                        const topKey = `top:${sourceNodeId}:${targetNodeId}:${this.minSepHeight}`;
                        const alignKey = `align-x:${targetNodeId}:${sourceNodeId}`;
                        if (!generatedConstraints.has(topKey) && !this.hasTransitivePath(aboveGraph, sourceNodeId, targetNodeId)) {
                            generatedConstraints.add(topKey);
                            this.addToTransitiveGraph(aboveGraph, sourceNodeId, targetNodeId);
                            constraints.push(this.topConstraint(sourceNodeId, targetNodeId, this.minSepHeight, layoutNodes, c));
                        }
                        if (!generatedConstraints.has(alignKey)) {
                            generatedConstraints.add(alignKey);
                            constraints.push(this.ensureSameXConstraint(targetNodeId, sourceNodeId, layoutNodes, c));
                        }
                    }
                });
            });
        });

        return { conjunctive: constraints, disjunctive: disjunctions };
    }

    /**
     * Produces the negated constraint alternatives for a single orientation direction on a tuple.
     *
     * Returns LayoutConstraint[][] — an array of alternatives for a DisjunctiveConstraint.
     *
     * For cardinal directions (above/below/left/right):
     *   NOT(ordering) = single flipped constraint → [[flipped]]
     *
     * For "directly" variants (directlyAbove = ordering AND alignment):
     *   ¬(ordering ∧ alignment) = ¬ordering ∨ ¬alignment
     *   ¬ordering = (reverse ∨ align) → 2 alternatives
     *   ¬alignment = (differ-alt1 ∨ differ-alt2) → 2 alternatives
     *   Total: 4 alternatives.
     */
    private negateOrientationDirection(
        direction: RelativeDirection,
        sourceNodeId: string, targetNodeId: string,
        layoutNodes: LayoutNode[], sourceConstraint: ConstraintSource
    ): LayoutConstraint[][] {
        // Cardinal directions: ¬(A < B) = (B < A) ∨ (A ≡ B)  →  2-alternative disjunction
        switch (direction) {
            case "left":
                return [
                    [this.leftConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameXConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                ];
            case "right":
                return [
                    [this.leftConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameXConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                ];
            case "above":
                return [
                    [this.topConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameYConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                ];
            case "below":
                return [
                    [this.topConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameYConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                ];

            // directly* variants: ¬(ordering ∧ alignment) = ¬ordering ∨ ¬alignment
            // ¬ordering = 2 alternatives (reverse ∨ align), ¬alignment = 2 alternatives → 4 total
            case "directlyLeft":
                // Positive: target < source on x ∧ same Y
                return [
                    [this.leftConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameXConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                    [this.topConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.topConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                ];
            case "directlyRight":
                // Positive: source < target on x ∧ same Y
                return [
                    [this.leftConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameXConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                    [this.topConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.topConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                ];
            case "directlyAbove":
                // Positive: target < source on y ∧ same X
                return [
                    [this.topConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameYConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                    [this.leftConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.leftConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                ];
            case "directlyBelow":
                // Positive: source < target on y ∧ same X
                return [
                    [this.topConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.ensureSameYConstraint(sourceNodeId, targetNodeId, layoutNodes, sourceConstraint)],
                    [this.leftConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, sourceConstraint)],
                    [this.leftConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, sourceConstraint)],
                ];
            default:
                return [];
        }
    }

    /**
     * Checks if there's a transitive path from source to target in the graph.
     * Used to detect redundant constraints via transitivity.
     * @param graph - Map of node to its reachable nodes
     * @param source - Source node
     * @param target - Target node
     * @returns True if path exists (BFS)
     */
    private hasTransitivePath(graph: Map<string, Set<string>>, source: string, target: string): boolean {
        const sourceReachable = graph.get(source);
        if (!sourceReachable) return false;
        
        // Direct connection
        if (sourceReachable.has(target)) return true;
        
        // BFS to find transitive path
        const visited = new Set<string>([source]);
        const queue = Array.from(sourceReachable);
        
        while (queue.length > 0) {
            const current = queue.shift()!;
            if (current === target) return true;
            if (visited.has(current)) continue;
            visited.add(current);
            
            const neighbors = graph.get(current);
            if (neighbors) {
                for (const neighbor of neighbors) {
                    if (!visited.has(neighbor)) {
                        queue.push(neighbor);
                    }
                }
            }
        }
        
        return false;
    }
    
    /**
     * Adds an edge to the transitive graph and updates transitive closure incrementally.
     * @param graph - Map of node to its reachable nodes
     * @param from - Source node
     * @param to - Target node
     */
    private addToTransitiveGraph(graph: Map<string, Set<string>>, from: string, to: string): void {
        // Add direct edge
        if (!graph.has(from)) {
            graph.set(from, new Set());
        }
        graph.get(from)!.add(to);
        
        // Update transitive closure: all nodes that reach 'from' can now also reach anything 'to' can reach
        const toReachable = graph.get(to) || new Set<string>();
        
        // Find all nodes that can reach 'from'
        for (const [node, reachable] of graph.entries()) {
            if (node === from || reachable.has(from)) {
                // This node can reach 'from', so it can also reach 'to' and everything 'to' reaches
                reachable.add(to);
                for (const transitiveTarget of toReachable) {
                    reachable.add(transitiveTarget);
                }
            }
        }
    }

    /**
     * Applies the align constraints to the layout nodes.
     * @param layoutNodes - The layout nodes to which the constraints will be applied.
     * @returns An array of layout constraints.
     */
    applyAlignConstraints(layoutNodes: LayoutNode[], g: Graph): {
        conjunctive: LayoutConstraint[];
        disjunctive: DisjunctiveConstraint[];
    } {
        let constraints: LayoutConstraint[] = [];
        let disjunctions: DisjunctiveConstraint[] = [];
        let alignConstraints = this._layoutSpec.constraints.alignment;

        // Track generated alignment constraints to avoid duplicates
        // Use normalized key (sorted node IDs) since alignment is symmetric
        const generatedAlignments = new Set<string>();

        alignConstraints.forEach((c: AlignConstraint, specIndex: number) => {
            let direction = c.direction;
            let selector = c.selector;

            let selectorRes;
            try {
                selectorRes = this.evaluator.evaluate(selector, { instanceIndex: this.instanceNum });
            } catch (error) {
                this.recordSelectorError(selector, 'align selector', error);
                return; // Skip this align constraint
            }
            if (!this.acceptSelectorResult(selectorRes, selector, 'align selector', 'binary', 'align', specIndex, c)) {
                return; // Skip this constraint only — every other one still applies
            }
            let selectedTuples: string[][] = selectorRes.selectedTwoples();

            // For each tuple, apply the alignment constraint
            selectedTuples.forEach((tuple) => {
                let sourceNodeId = tuple[0];
                let targetNodeId = tuple[1];

                // Skip tuples referencing hidden nodes; record the conflict for error reporting
                const sourceHidden = this.isNodeHiddenByDirective(sourceNodeId);
                const targetHidden = this.isNodeHiddenByDirective(targetNodeId);
                if (sourceHidden || targetHidden) {
                    const alignDir = direction === 'horizontal' ? 'horizontally' : 'vertically';
                    const description = `${sourceNodeId} is ${alignDir} aligned with ${targetNodeId}`;
                    this.recordHiddenNodeConflict(c, description, sourceNodeId, targetNodeId, sourceHidden, targetHidden);
                    return; // Skip this tuple
                }

                // Add alignment edge for align constraints if enabled AND edge doesn't already exist in the graph
                if (this.shouldAddAlignmentEdge(g, sourceNodeId, targetNodeId)) {
                    const alignmentEdgeLabel = `_alignment_${sourceNodeId}_${targetNodeId}_`;
                    g.setEdge(sourceNodeId, targetNodeId, alignmentEdgeLabel, alignmentEdgeLabel);
                }

                // For negated alignment: NOT(same-Y) = must differ on Y = above OR below (disjunction)
                if (c.negated) {
                    const [node1, node2] = [sourceNodeId, targetNodeId].sort();
                    const key = `not-align-${direction === 'horizontal' ? 'y' : 'x'}:${node1}:${node2}`;
                    if (!generatedAlignments.has(key)) {
                        generatedAlignments.add(key);
                        if (direction === "horizontal") {
                            // NOT same-Y → source above target OR target above source
                            const alt1 = [this.topConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, c)];
                            const alt2 = [this.topConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, c)];
                            disjunctions.push(new DisjunctiveConstraint(c, [alt1, alt2]));
                        } else {
                            // NOT same-X → source left of target OR target left of source
                            const alt1 = [this.leftConstraint(sourceNodeId, targetNodeId, 0, layoutNodes, c)];
                            const alt2 = [this.leftConstraint(targetNodeId, sourceNodeId, 0, layoutNodes, c)];
                            disjunctions.push(new DisjunctiveConstraint(c, [alt1, alt2]));
                        }
                    }
                    return; // Skip positive alignment logic
                }

                if (direction === "horizontal") {
                    // Horizontal alignment means same Y coordinate
                    // Normalize node order for key (alignment is symmetric)
                    const [node1, node2] = [sourceNodeId, targetNodeId].sort();
                    const key = `align-y:${node1}:${node2}`;
                    if (!generatedAlignments.has(key)) {
                        generatedAlignments.add(key);
                        constraints.push(this.ensureSameYConstraint(sourceNodeId, targetNodeId, layoutNodes, c));
                    }
                } else if (direction === "vertical") {
                    // Vertical alignment means same X coordinate
                    const [node1, node2] = [sourceNodeId, targetNodeId].sort();
                    const key = `align-x:${node1}:${node2}`;
                    if (!generatedAlignments.has(key)) {
                        generatedAlignments.add(key);
                        constraints.push(this.ensureSameXConstraint(sourceNodeId, targetNodeId, layoutNodes, c));
                    }
                }
            });
        });

        return { conjunctive: constraints, disjunctive: disjunctions };
    }




    /**
     * Checks if there's already a direct edge (bidirectional) between two nodes in the graph.
     * @param g - The graph to check
     * @param sourceNodeId - First node ID
     * @param targetNodeId - Second node ID
     * @returns true if there's already an edge between the nodes
     */
    /**
     * Checks if two nodes should have an alignment edge added based on the current strategy.
     * 
     * @param g - The graph to check
     * @param sourceNodeId - First node ID
     * @param targetNodeId - Second node ID
     * @returns true if an alignment edge should be added (nodes are not connected according to strategy)
     */
    private shouldAddAlignmentEdge(g: Graph, sourceNodeId: string, targetNodeId: string): boolean {
        // If strategy is NEVER, never add alignment edges
        if (this.alignmentEdgeStrategy === AlignmentEdgeStrategy.NEVER) {
            return false;
        }

        // Check for direct edge
        const hasDirectEdge = this.hasDirectEdgeBetween(g, sourceNodeId, targetNodeId);
        
        // If strategy is DIRECT, only check for direct edges
        if (this.alignmentEdgeStrategy === AlignmentEdgeStrategy.DIRECT) {
            return !hasDirectEdge;
        }

        // Strategy is CONNECTED: check if nodes are connected via any path
        // If they have a direct edge, they're connected
        if (hasDirectEdge) {
            return false;
        }

        // Check if connected via any path (including alignment edges)
        return !this.isConnectedViaPath(g, sourceNodeId, targetNodeId);
    }

    /**
     * Checks if there's a direct edge between two nodes (either direction).
     * 
     * @param g - The graph to check
     * @param sourceNodeId - First node ID
     * @param targetNodeId - Second node ID
     * @returns true if there's a direct edge between the nodes
     */
    private hasDirectEdgeBetween(g: Graph, sourceNodeId: string, targetNodeId: string): boolean {
        // Direct edge check (either direction). Prefer graphlib.hasEdge if available.
        return (typeof (g as any).hasEdge === 'function' && ((g as any).hasEdge(sourceNodeId, targetNodeId) || (g as any).hasEdge(targetNodeId, sourceNodeId)))
            ||
            // fallback to scanning in/out edges (handles multi-edges)
            ((g.inEdges(sourceNodeId) || []).some(e => e.v === targetNodeId) ||
             (g.outEdges(sourceNodeId) || []).some(e => e.w === targetNodeId) ||
             (g.inEdges(targetNodeId) || []).some(e => e.v === sourceNodeId) ||
             (g.outEdges(targetNodeId) || []).some(e => e.w === sourceNodeId));
    }

    /**
     * Checks if two nodes are connected via any path in the graph.
     * This is used to determine whether to add alignment edges for WebCola.
     * 
     * Performance optimization: We skip alignment edges when nodes are already
     * connected via any path (not just directly connected). This significantly
     * reduces the number of constraints for large graphs while maintaining good
     * layout quality, as connected nodes are less likely to fall into bad local minima.
     * 
     * Note: This follows ALL edges including alignment edges (edges with _alignment_ prefix).
     * This ensures we don't add redundant alignment edges when nodes are already connected
     * via other alignment edges.
     * 
     * @param g - The graph to check
     * @param sourceNodeId - First node ID
     * @param targetNodeId - Second node ID
     * @param excludeEdge - Optional edge to exclude from the connectivity check (for pruning)
     * @returns true if there's any path between the nodes (treating graph as undirected)
     */
    private isConnectedViaPath(g: Graph, sourceNodeId: string, targetNodeId: string, excludeEdge?: { v: string, w: string, name?: string }): boolean {
        // BFS treating graph as undirected (follow predecessors and successors)
        const visited = new Set<string>();
        const queue: string[] = [sourceNodeId];

        while (queue.length > 0) {
            const cur = queue.shift()!;
            if (cur === targetNodeId) {
                return true; // Connected via path
            }
            if (visited.has(cur)) continue;
            visited.add(cur);

            // Get all edges from current node
            const outEdges = g.outEdges(cur) || [];
            const inEdges = g.inEdges(cur) || [];
            
            // Process all edges, excluding the specified edge if provided
            for (const edge of [...outEdges, ...inEdges]) {
                // Skip the excluded edge if specified
                if (excludeEdge && 
                    ((edge.v === excludeEdge.v && edge.w === excludeEdge.w && edge.name === excludeEdge.name) ||
                     (edge.v === excludeEdge.w && edge.w === excludeEdge.v && edge.name === excludeEdge.name))) {
                    continue;
                }
                
                // Add the neighbor to the queue
                const neighbor = edge.v === cur ? edge.w : edge.v;
                if (!visited.has(neighbor)) {
                    queue.push(neighbor);
                }
            }
        }

        return false; // Not connected
    }

    /**
     * Prunes redundant alignment edges from the graph after all alignment edges have been added.
     * An alignment edge is redundant if removing it doesn't disconnect the nodes it connects.
     * 
     * This is useful for the case where alignment constraints create cycles or multiple paths,
     * and some edges can be removed without affecting connectivity.
     * 
     * Example: If a→b→c→a forms a cycle of alignment edges, one edge can be removed.
     * 
     * @param g - The graph containing alignment edges
     */
    private pruneRedundantAlignmentEdges(g: Graph): void {
        // Only prune if strategy is CONNECTED (otherwise we might break the intended behavior)
        if (this.alignmentEdgeStrategy !== AlignmentEdgeStrategy.CONNECTED) {
            return;
        }

        // Collect all alignment edges
        const alignmentEdges = g.edges().filter(edge => {
            const edgeId = edge.name;
            return edgeId && edgeId.includes('_alignment_');
        });

        // Track which edges we've removed
        const removedEdges: typeof alignmentEdges = [];

        // Try to remove each alignment edge
        for (const edge of alignmentEdges) {
            // Check if nodes are still connected without this edge
            if (this.isConnectedViaPath(g, edge.v, edge.w, edge)) {
                // Nodes are still connected, so this edge is redundant
                g.removeEdge(edge.v, edge.w, edge.name);
                removedEdges.push(edge);
            }
        }

        // Log pruning statistics if any edges were removed
        if (removedEdges.length > 0) {
            console.log(`Pruned ${removedEdges.length} redundant alignment edges out of ${alignmentEdges.length} total alignment edges`);
        }
    }

    private getDisconnectedNodes(g: Graph): string[] {
        let inNodes = g.edges().map(edge => edge.w);
        let outNodes = g.edges().map(edge => edge.v);

        // All nodes in the graph
        let allNodes = new Set(g.nodes());
        let allConnectedNodes = new Set([...inNodes, ...outNodes]);
        let disconnectedNodes = [...allNodes].filter(node => !allConnectedNodes.has(node));
        return disconnectedNodes;
    }

    private buildLayoutEdges(g: Graph, layoutNodes: LayoutNode[], groups: LayoutGroup[] = []): LayoutEdge[] {
        // Map each group's name → its key node. Group edges are labelled with the
        // group name, so this lets us stamp the true key (anchor) end regardless
        // of which way round the underlying graphlib edge was created (the key is
        // the source for 'togroup', the target for 'fromgroup').
        const keyNodeByGroupName = new Map<string, string>();
        // Map each group's name → the connector styling authored on its
        // `addEdge` block (the connector is an edge, so it carries a lineStyle +
        // textStyle). Sourced from the GroupBySelector that created the group.
        const connectorStyleByGroupName = new Map<string, { lineStyle?: LineStyle, textStyle?: TextStyle }>();
        for (const grp of groups) {
            if (grp.keyNodeId != null) keyNodeByGroupName.set(grp.name, grp.keyNodeId);
            const src = grp.sourceConstraint;
            if (src instanceof GroupBySelector && (src.connectorLineStyle || src.connectorTextStyle)) {
                connectorStyleByGroupName.set(grp.name, {
                    lineStyle: src.connectorLineStyle,
                    textStyle: src.connectorTextStyle,
                });
            }
        }

        return g.edges().map((edge) => {

            const edgeId = edge.name;
            const edgeLabel: string = g.edge(edge.v, edge.w, edgeId);
            let source = layoutNodes.find((node) => node.id === edge.v);
            let target = layoutNodes.find((node) => node.id === edge.w);
            let relName = this.getRelationName(g, edge);

            // For a group edge, the key (anchor) node is looked up from the group rather
            // than assumed to be edge.v: a 'fromgroup' edge is created member→key, so its
            // graph source is the member. Edge directives are written against the original
            // selector-tuple order (key, member), so canonicalise the (source, target) atoms
            // back to that order for directive lookup — directives then apply regardless of
            // which way the arrow is drawn. The drawn arrow still follows the graph edge
            // (source/target below), and keyNodeId records the true anchor for the renderer.
            const isGroupEdge = !!edgeId && edgeId.startsWith('_g_');
            const groupKey = isGroupEdge ? (keyNodeByGroupName.get(edgeLabel) ?? edge.v) : undefined;
            const dirSource = groupKey !== undefined ? groupKey : edge.v;
            const dirTarget = groupKey !== undefined ? (groupKey === edge.v ? edge.w : edge.v) : edge.w;

            // A group connector edge (`_g_`) is styled by its group's `addEdge`
            // block; that authored style wins over any incidental edgeStyle match.
            const connStyle = isGroupEdge ? connectorStyleByGroupName.get(edgeLabel) : undefined;
            const connLine = connStyle?.lineStyle;

            // edgeStyle (resolver-based) takes precedence per-property; whatever it
            // leaves unset falls back to the legacy inferredEdge/edgeColor path.
            const styled = this.resolveEdgeStyleForEdge(relName, dirSource, dirTarget);
            let color = connLine?.color ?? styled.lineStyle?.color ?? this.getEdgeColor(relName, dirSource, dirTarget, edgeId);
            let style = connLine?.pattern ?? styled.lineStyle?.pattern ?? this.getEdgeStyle(relName, dirSource, dirTarget, edgeId);
            let weight = connLine?.weight ?? styled.lineStyle?.weight ?? this.getEdgeWeight(relName, dirSource, dirTarget, edgeId);
            let showLabel = styled.showLabel ?? this.getEdgeShowLabel(relName, dirSource, dirTarget, edgeId);
            let highlight = connLine?.highlight ?? styled.lineStyle?.highlight ?? this.getEdgeHighlight(relName, dirSource, dirTarget, edgeId);
            // Edge-label styling: a group connector's own textStyle wins, then an
            // inferred edge's, then the resolved edgeStyle (edgeColor has none).
            let textStyle = connStyle?.textStyle ?? this.getInferredEdgeDirective(edgeId)?.textStyle ?? styled.textStyle;

            // Skip edges with missing source or target nodes
            if (!source || !target || !edgeId) {
                return null;
            }

            let e: LayoutEdge = {
                source: source,
                target: target,
                label: edgeLabel,
                relationName: relName,
                id: edgeId,
                color: color,
                style: style,
                weight: weight,
                showLabel: showLabel,
                highlight: highlight,
                textStyle: textStyle,
                // For group edges the graphlib edge label IS the group name (see constructGroupEdgeID).
                // Informational only since the per-end stamp unification: the renderer
                // routes off sourceGroupId/targetGroupId; consumers (e.g. the explorer)
                // still use groupId to identify connector edges.
                groupId: isGroupEdge ? edgeLabel : undefined,
                // The group's key (anchor) node (computed above). Informational only:
                // the renderer no longer derives the snap side from it (the per-end
                // stamps below carry that), but the field is kept for consumers.
                keyNodeId: groupKey,
                // Per-end group attachments — the renderer's single group-edge contract.
                // A stamped end renders at that group's hull; unstamped ends render at
                // their node. `addEdge` connectors (_g_) desugar here: the key side is
                // the plain anchor, so the OTHER side is the group end ('togroup' runs
                // key→member ⇒ target stamped; 'fromgroup' member→key ⇒ source stamped).
                // A group-by-field connector whose key is a middle atom of an n-ary
                // tuple matches neither side and stays unstamped — it renders
                // node-to-node, exactly as the legacy path left it.
                sourceGroupId: isGroupEdge
                    ? (groupKey === edge.v ? undefined : groupKey === edge.w ? edgeLabel : undefined)
                    : this.inferredEdgeGroupStamps.get(edgeId)?.sourceGroupId,
                targetGroupId: isGroupEdge
                    ? (groupKey === edge.v ? edgeLabel : undefined)
                    : this.inferredEdgeGroupStamps.get(edgeId)?.targetGroupId,
            };
            return e;
        }).filter((edge): edge is LayoutEdge => edge !== null);
    }

    private filterHiddenEdges(layoutEdges: LayoutEdge[]): LayoutEdge[] {
        return layoutEdges.filter((edge) => !edge.id.startsWith(this.hideThisEdge));
    }



    private getNodeFromId(nodeId: string, layoutNodes: LayoutNode[], sourceConstraint?: ConstraintSource): LayoutNode {
        let node = layoutNodes.find((node) => node.id === nodeId);
        if (!node) {
            throw new MissingNodeConstraintError(nodeId, sourceConstraint);
        }
        return node;
    }


    private leftConstraint(leftId: string, rightId: string, minDistance: number, layoutNodes: LayoutNode[], sourceConstraint: ConstraintSource): LeftConstraint {

        let left = this.getNodeFromId(leftId, layoutNodes, sourceConstraint);
        let right = this.getNodeFromId(rightId, layoutNodes, sourceConstraint);
        return { left: left, right: right, minDistance: minDistance, sourceConstraint: sourceConstraint };
    }

    private topConstraint(topId: string, bottomId: string, minDistance: number, layoutNodes: LayoutNode[], sourceConstraint: ConstraintSource): TopConstraint {

        let top = this.getNodeFromId(topId, layoutNodes, sourceConstraint);
        let bottom = this.getNodeFromId(bottomId, layoutNodes, sourceConstraint);

        return { top: top, bottom: bottom, minDistance: minDistance, sourceConstraint: sourceConstraint };
    }

    private ensureSameYConstraint(node1Id: string, node2Id: string, layoutNodes: LayoutNode[], sourceConstraint: ConstraintSource): AlignmentConstraint {

        let node1 = this.getNodeFromId(node1Id, layoutNodes, sourceConstraint);
        let node2 = this.getNodeFromId(node2Id, layoutNodes, sourceConstraint);

        return { axis: "y", node1: node1, node2: node2, sourceConstraint: sourceConstraint };
    }

    private ensureSameXConstraint(node1Id: string, node2Id: string, layoutNodes: LayoutNode[], sourceConstraint: ConstraintSource): AlignmentConstraint {

        let node1 = this.getNodeFromId(node1Id, layoutNodes, sourceConstraint);
        let node2 = this.getNodeFromId(node2Id, layoutNodes, sourceConstraint);

        return { axis: "x", node1: node1, node2: node2, sourceConstraint: sourceConstraint };
    }

    private singletonGroup(nodeId: string): LayoutGroup {

        let groupName = `${LayoutInstance.DISCONNECTED_PREFIX}${nodeId}`;

        return {
            name: groupName,
            nodeIds: [nodeId],
            keyNodeId: nodeId,
            showLabel: false
        }
    }



    /**
     * Compute, for every node in `g`, the strings that the renderer will draw
     * inside its box: main label, attribute lines, and Skolem-label lines.
     * These are the inputs to `estimateLabelBox` so the auto-sized default
     * matches what the user will actually see.
     *
     * Line formatting mirrors `webcola-cnd-graph.ts`:
     *   - main label = `g.node(nodeId).label || nodeId`
     *   - each attribute key produces one line: `"key: v1, v2, ..."`
     *   - each Skolem-label key produces one line: `"v1, v2, ..."` (key omitted)
     */
    private getNodeDisplayContent(
        g: Graph,
        ai: IDataInstance,
        attributes: Record<string, Record<string, string[]>>,
        attributeTextStyles: Record<string, Record<string, TextStyle>>
    ): Record<string, NodeDisplayContent> {
        const result: Record<string, NodeDisplayContent> = {};
        const atoms = ai.getAtoms();

        for (const nodeId of g.nodes()) {
            const nodeMetadata = g.node(nodeId);
            const label = nodeMetadata?.label || nodeId;

            const nodeAttrs = attributes[nodeId] || {};
            const nodeStyles = attributeTextStyles[nodeId] || {};
            // Sort once so line text and its font size stay index-aligned.
            const sortedAttrEntries = Object.entries(nodeAttrs)
                .sort(([a], [b]) => a.localeCompare(b));
            const attributeLines = sortedAttrEntries.map(([key, values]) => `${key}: ${values.join(', ')}`);
            const attributeFontSizes = sortedAttrEntries.map(([key]) => resolveAttrFontSize(nodeStyles[key]?.size));

            const atom = atoms.find((x) => x.id === nodeId);
            const skolemLines: string[] = [];
            if (atom?.labels) {
                for (const values of Object.values(atom.labels)) {
                    skolemLines.push(Array.isArray(values) ? values.join(', ') : String(values));
                }
            }

            result[nodeId] = { label, attributeLines, attributeFontSizes, skolemLines };
        }
        return result;
    }

    private getNodeSizeMap(
        g: Graph,
        contentByNode: Record<string, NodeDisplayContent>
    ): Record<string, { width: number; height: number }> {
        let nodeSizeMap: Record<string, { width: number; height: number }> = {};

        // Apply size directives first. These come from `size` constraints in
        // the layout spec and must produce exactly the requested dimensions
        // (matching `sat_size` in the Lean mechanization: ∀ a ∈ S, b.width = w
        // ∧ b.height = h).
        let sizeDirectives = this._layoutSpec.directives.sizes;
        sizeDirectives.forEach((sizeDirective, specIndex) => {
            let selectedNodes: string[];
            try {
                const selectorRes = this.evaluator.evaluate(sizeDirective.selector, { instanceIndex: this.instanceNum });
                if (!this.acceptSelectorResult(selectorRes, sizeDirective.selector, 'size selector', 'unary', 'size', specIndex)) {
                    return; // Skip this directive only
                }
                selectedNodes = selectorRes.selectedAtoms();
            } catch (error) {
                this.recordSelectorError(sizeDirective.selector, 'size selector', error);
                return; // Skip this size directive
            }
            let width = sizeDirective.width;
            let height = sizeDirective.height;

            selectedNodes.forEach((nodeId) => {

                if (nodeSizeMap[nodeId]) {

                    const existingSize = nodeSizeMap[nodeId];
                    if (existingSize.width !== width || existingSize.height !== height) {
                        throw new Error(
                            `Size Conflict: "${nodeId}" cannot have multiple sizes: ${JSON.stringify(existingSize)}, ${JSON.stringify({ width, height })}.`
                        );
                    }
                }

                nodeSizeMap[nodeId] = { width: width, height: height };
            });
        });

        // Auto-size nodes that no `size` directive matched. The estimator
        // floor is DEFAULT_NODE_WIDTH × DEFAULT_NODE_HEIGHT (100 × 60), so
        // short-label nodes keep their historical footprint and only
        // long-label nodes grow.
        const floor = { w: this.DEFAULT_NODE_WIDTH, h: this.DEFAULT_NODE_HEIGHT };
        for (const nodeId of g.nodes()) {
            if (nodeSizeMap[nodeId]) continue;
            const c = contentByNode[nodeId];
            const main = c?.label ?? '';
            // Attribute lines carry their own tier so the box fits large/small
            // text; Skolem lines default to the normal secondary size.
            const secondary: SecondaryLine[] = c
                ? [
                    ...c.attributeLines.map((text, i) => ({ text, fontSize: c.attributeFontSizes[i] })),
                    ...c.skolemLines,
                ]
                : [];
            nodeSizeMap[nodeId] = estimateLabelBox(main, secondary, { min: floor });
        }

        return nodeSizeMap;
    }


    /**
     * Resolve every atom's `atomStyle` into one concrete style. Each rule's
     * selector is evaluated to the atoms it matches (absent selector = all
     * atoms), inverting to a per-atom list of contributing rules; each atom's
     * rules are then folded through the shared resolver. Because a supertype
     * selector already returns subtype atoms, gap-fill inheritance up the type
     * ancestry and the no-silent-override collision both fall out of that fold —
     * a genuine leaf disagreement throws {@link StyleCollisionError} (the 3.0
     * contract, surfaced like the old color conflict). Only atoms with at least
     * one matching rule appear in the map. Legacy `atomColor` desugars into these
     * rules upstream (border-preserving: value→borderStyle.color).
     */
    private getAtomStyleMap(g: Graph, _a: IDataInstance): Record<string, AtomStyleSpec> {
        const rules = this._layoutSpec.directives.atomStyles;
        const styleMap: Record<string, AtomStyleSpec> = {};
        if (rules.length === 0) return styleMap;

        const allNodeIds = [...g.nodes()];
        const allNodeIdSet = new Set(allNodeIds);
        const rulesByNode: Record<string, AtomStyleRule[]> = {};

        rules.forEach((rule, specIndex) => {
            let matched: string[];
            if (!rule.selector) {
                matched = allNodeIds; // no selector → applies to every atom
            } else {
                try {
                    const selectorRes = this.evaluator.evaluate(rule.selector, { instanceIndex: this.instanceNum });
                    if (!this.acceptSelectorResult(selectorRes, rule.selector, 'atomStyle selector', 'unary', 'atomStyle', specIndex)) {
                        return; // Skip this rule only
                    }
                    matched = selectorRes.selectedAtoms();
                } catch (error) {
                    this.recordSelectorError(rule.selector, 'atomStyle selector', error);
                    return; // Skip this rule
                }
            }
            matched.forEach((nodeId) => {
                // Selectors can name atoms that aren't graph nodes (e.g. hidden);
                // only style atoms actually being laid out.
                if (!allNodeIdSet.has(nodeId)) return;
                if (!rulesByNode[nodeId]) rulesByNode[nodeId] = [];
                rulesByNode[nodeId].push(rule);
            });
        });

        for (const nodeId of allNodeIds) {
            const nodeRules = rulesByNode[nodeId];
            if (!nodeRules || nodeRules.length === 0) continue;
            // Throws StyleCollisionError on a genuine leaf disagreement.
            styleMap[nodeId] = resolveAtomStyle(nodeRules, `atom "${nodeId}"`);
        }

        return styleMap;
    }

    /**
     * Builds the node→color map (the border/outline color) and records which
     * nodes were colored by an explicit directive (vs. the default type palette /
     * black fallback). The renderer uses `explicitlyColored` to decide which
     * colors may be retuned for a themed canvas and which must be preserved.
     *
     * The border color comes from the resolved `atomStyle` (`borderStyle.color`)
     * — which legacy `atomColor` desugars into — so this reads `atomStyleMap`
     * rather than re-walking directives. Collision detection lives in the
     * resolver (a leaf disagreement already threw in {@link getAtomStyleMap}).
     */
    private getNodeColorMap(g: Graph, a: IDataInstance, atomStyleMap: Record<string, AtomStyleSpec>): { colorMap: Record<string, string>, explicitlyColored: Set<string> } {
        let nodeColorMap: Record<string, string> = {};
        let explicitlyColored = new Set<string>();

        // Start by getting the default signature colors
        let sigColors = this.getSigColors(a);

        let graphNodes = [...g.nodes()];

        // A resolved borderStyle.color is the node's explicit outline color.
        graphNodes.forEach((nodeId) => {
            const borderColor = atomStyleMap[nodeId]?.borderStyle?.color;
            if (borderColor) {
                nodeColorMap[nodeId] = borderColor;
                explicitlyColored.add(nodeId);
            }
        });

        // Set default colors for nodes that do not have a color set
        graphNodes.forEach((nodeId) => {
            if (!nodeColorMap[nodeId]) {
                let mostSpecificType = this.getMostSpecificType(nodeId, a);
                nodeColorMap[nodeId] = sigColors[mostSpecificType];
            }
        });

        return { colorMap: nodeColorMap, explicitlyColored };
    }

    /**
     * Gets the color for a specific edge based on directives.
     * @param relName - The relation name of the edge.
     * @param sourceAtom - The source atom ID.
     * @param targetAtom - The target atom ID.
     * @param edgeId - The edge ID (optional, used to identify inferred edges).
     * @returns The color for the edge, or "black" as default.
     */
    private getEdgeColor(relName: string, sourceAtom: string, targetAtom: string, edgeId?: string): string {
        const inferredDirective = this.getInferredEdgeDirective(edgeId);
        if (inferredDirective?.color) {
            return inferredDirective.color;
        }

        const directive = this.findEdgeDirective(relName, sourceAtom, targetAtom);
        if (directive?.color) {
            return directive.color;
        }

        return "black"; // Default color
    }

    private getEdgeStyle(relName: string, sourceAtom: string, targetAtom: string, edgeId?: string): EdgeStyle | undefined {
        const inferredDirective = this.getInferredEdgeDirective(edgeId);
        const inferredStyle = normalizeEdgeStyle(inferredDirective?.style);
        if (inferredStyle) {
            return inferredStyle;
        }

        const directive = this.findEdgeDirective(relName, sourceAtom, targetAtom);
        return normalizeEdgeStyle(directive?.style);
    }

    private getEdgeHighlight(relName: string, sourceAtom: string, targetAtom: string, edgeId?: string): string | undefined {
        const inferredDirective = this.getInferredEdgeDirective(edgeId);
        if (inferredDirective?.highlight) {
            return inferredDirective.highlight;
        }

        const directive = this.findEdgeDirective(relName, sourceAtom, targetAtom);
        return directive?.highlight;
    }

    private getEdgeWeight(relName: string, sourceAtom: string, targetAtom: string, edgeId?: string): number | undefined {
        const inferredDirective = this.getInferredEdgeDirective(edgeId);
        const inferredWeight = this.normalizeEdgeWeight(inferredDirective?.weight, "inferred edge");
        if (inferredWeight !== undefined) {
            return inferredWeight;
        }

        const directive = this.findEdgeDirective(relName, sourceAtom, targetAtom);
        return this.normalizeEdgeWeight(directive?.weight, "edge");
    }

    /**
     * Gets whether the label should be shown for a specific edge based on directives.
     * @param relName - The relation name of the edge.
     * @param sourceAtom - The source atom ID.
     * @param targetAtom - The target atom ID.
     * @param edgeId - The edge ID (optional, used to identify inferred edges).
     * @returns true if the label should be shown, false if hidden, undefined to use default.
     *          Note: Inferred edges always show labels, so this only applies to regular edges.
     */
    private getEdgeShowLabel(relName: string, sourceAtom: string, targetAtom: string, _edgeId?: string): boolean | undefined {
        // Inferred edges always show labels - no showLabel check needed
        const directive = this.findEdgeDirective(relName, sourceAtom, targetAtom);
        return directive?.showLabel;
    }

    private getInferredEdgeDirective(edgeId?: string): InferredEdgeDirective | undefined {
        const inferredEdgePrefix = "_inferred_";
        if (!edgeId || !edgeId.includes(inferredEdgePrefix)) {
            return undefined;
        }

        const inferredEdges = this._layoutSpec.directives.inferredEdges;
        return inferredEdges.find((directive) => edgeId.includes(`${inferredEdgePrefix}<:${directive.name}`));
    }

    /**
     * Whether an edge directive (edgeColor or edgeStyle — anything keyed by
     * field + optional selector/filter) applies to the edge (relName, source→target).
     * Selector matches on the source atom; filter matches on the (source, target) tuple.
     *
     * `findEdgeDirective` below still inlines this same logic for edgeColor; that
     * duplication goes away when edgeColor is shimmed onto edgeStyle.
     */
    private edgeDirectiveMatches(
        directive: { field: string; selector?: string; filter?: string },
        relName: string,
        sourceAtom: string,
        targetAtom?: string,
    ): boolean {
        if (directive.field !== relName) {
            return false;
        }
        if (directive.selector) {
            try {
                const selectorResult = this.evaluator.evaluate(directive.selector, { instanceIndex: this.instanceNum });
                if (!selectorResult.selectedAtoms().includes(sourceAtom)) {
                    return false;
                }
            } catch (error) {
                this.recordSelectorError(directive.selector, 'edge selector', error);
                return false;
            }
        }
        if (directive.filter && targetAtom) {
            try {
                const filterResult = this.evaluator.evaluate(directive.filter, { instanceIndex: this.instanceNum });
                const matched = filterResult.selectedTwoples().some(
                    tuple => tuple[0] === sourceAtom && tuple[1] === targetAtom
                );
                if (!matched) {
                    return false;
                }
            } catch (error) {
                this.recordSelectorError(directive.filter, 'edge filter', error);
                return false;
            }
        }
        return true;
    }

    /**
     * Resolve the edgeStyle directives matching an edge into one concrete style.
     * Overlapping edgeStyle rules compose leaf-wise; a genuine disagreement throws
     * StyleCollisionError (no silent override). Empty when nothing matches, so callers
     * fall back to the legacy inferredEdge/edgeColor path.
     */
    private resolveEdgeStyleForEdge(relName: string, sourceAtom: string, targetAtom: string): EdgeStyleSpec {
        const rules = this._layoutSpec.directives.edgeStyles.filter(
            rule => this.edgeDirectiveMatches(rule, relName, sourceAtom, targetAtom)
        );
        if (rules.length === 0) {
            return {};
        }
        return resolveEdgeStyle(rules, `edge ${relName} (${sourceAtom} → ${targetAtom})`);
    }

    private findEdgeDirective(relName: string, sourceAtom: string, targetAtom?: string): EdgeColorDirective | undefined {
        const colorDirectives = this._layoutSpec.directives.edgeColors;
        
        for (const directive of colorDirectives) {
            if (directive.field !== relName) {
                continue;
            }
            
            // Check if selector matches (or no selector means match all sources)
            let selectorMatches = true;
            if (directive.selector) {
                try {
                    const selectorResult = this.evaluator.evaluate(directive.selector, { instanceIndex: this.instanceNum });
                    const selectedAtoms = selectorResult.selectedAtoms();
                    selectorMatches = selectedAtoms.includes(sourceAtom);
                } catch (error) {
                    this.recordSelectorError(directive.selector, 'edge selector', error);
                    selectorMatches = false;
                }
            }
            
            if (!selectorMatches) {
                continue;
            }
            
            // Check if filter matches (or no filter means match all tuples)
            let filterMatches = true;
            if (directive.filter && targetAtom) {
                try {
                    const filterResult = this.evaluator.evaluate(directive.filter, { instanceIndex: this.instanceNum });
                    const selectedTuples = filterResult.selectedTwoples();
                    // Check if the (source, target) pair is in the filtered set
                    filterMatches = selectedTuples.some(
                        tuple => tuple[0] === sourceAtom && tuple[1] === targetAtom
                    );
                } catch (error) {
                    this.recordSelectorError(directive.filter, 'edge filter', error);
                    filterMatches = false;
                }
            }
            
            if (selectorMatches && filterMatches) {
                return directive;
            }
        }

        return undefined;
    }

    private normalizeEdgeWeight(weight: unknown, context: string): number | undefined {
        if (weight === undefined || weight === null) {
            return undefined;
        }

        if (typeof weight !== "number" || Number.isNaN(weight) || weight <= 0) {
            console.warn(`Ignoring ${context} weight because it is not a positive number: ${weight}`);
            return undefined;
        }

        return weight;
    }

    /**
     * Obtains the default sig colors for each sig type.
     * @param ai - The Alloy instance to get the sig colors for.
     * @returns a Record mapping sig types to their colors.
     */
    private getSigColors(ai: IDataInstance): Record<string, string> {
        let sigColors: Record<string, string> = {};

        let types = ai.getTypes();
        let colorPicker = new ColorPicker(types.length);
        types.forEach((type) => {
            sigColors[type.id] = colorPicker.getNextColor();
        });
        return sigColors;
    }

    private getFieldTuples(a: IDataInstance, fieldName: string): string[][] {

        let relations = a.getRelations();
        let field = Object.values(relations).find((rel) => rel.name === fieldName);


        if (!field) {
            return [];
        }

        let fieldTuples = field.tuples.map((tuple) => {
            return tuple.atoms;
        });
        return fieldTuples;
    }

    private getFieldTuplesForSourceAndTarget(a: IDataInstance, fieldName: string, src: string, tgt: string): string[][] {

        let fieldTuples = this.getFieldTuples(a, fieldName);
        let filteredTuples = fieldTuples.filter((tuple) => {
            let arity = tuple.length;
            if (arity < 1) {
                return false;
            }
            return tuple[0] === src && tuple[arity - 1] === tgt;
        });

        return filteredTuples;

    }

    // g is an inout parameter. I.E. it will be modified.
    private addinferredEdges(g: Graph) {

        const inferredEdgePrefix = "_inferred_";
        // Draw-qualified directives need groups to resolve their endpoints, and
        // groups don't exist yet at this point — they run in a second pass
        // (addDrawInferredEdges) after generateGroups().
        // Iterate the full list and skip `draw` directives inline, so specIndex stays
        // a position in directives.inferredEdges rather than in a filtered copy.
        this._layoutSpec.directives.inferredEdges.forEach((he, specIndex) => {
            if (he.draw) {
                return;
            }

            let res;
            try {
                res = this.evaluator.evaluate(he.selector, { instanceIndex: this.instanceNum });
            } catch (error) {
                this.recordSelectorError(he.selector, 'inferredEdge selector', error);
                return; // Skip this inferred edge
            }
            if (!this.acceptSelectorResult(res, he.selector, 'inferredEdge selector', 'any', 'inferredEdge', specIndex)) {
                return; // Skip this directive only — every other one still applies
            }

            let selectedTuples: string[][] = res.selectedTuplesAll();
            let edgeIdPrefix = `${inferredEdgePrefix}<:${he.name}`;

            selectedTuples.forEach((tuple) => {

                let n = tuple.length;

                let sourceNodeId = tuple[0];
                let targetNodeId = tuple[n - 1];

               let edgeLabel = he.name;

                // Use node labels for middle nodes instead of IDs
                if (n > 2) {
                    // Get labels for middle nodes
                    let middleNodeLabels = tuple.slice(1, n - 1).map(nodeId => {
                        const nodeMetadata = g.node(nodeId);
                        return nodeMetadata?.label || nodeId; // Use label if available, fallback to ID
                    }).join(",");
                    edgeLabel = `${edgeLabel}[${middleNodeLabels}]`;
                }
                // The edge 

                let fullTuple = tuple.join("->");

                let edgeId = `${edgeIdPrefix}<:${fullTuple}`;
                g.setEdge(sourceNodeId, targetNodeId, edgeLabel, edgeId);
            });
        });
    }

    /**
     * Second inferred-edge pass, for directives with a `draw` field. Runs after
     * generateGroups() because group endpoints resolve against the generated
     * groups: a `draw` end naming group family `F` attaches to the group
     * `F[<end's atom>]` — matched structurally as (constraint name, keyNodeId),
     * never by parsing display names.
     *
     * The graphlib edge anchors on real nodes (a group member for group ends —
     * NOT the key, which is commonly hidden); the group attachment itself is
     * recorded in inferredEdgeGroupStamps and stamped onto the LayoutEdge, and
     * the renderer snaps stamped ends to the group hull.
     */
    private addDrawInferredEdges(g: Graph, groups: LayoutGroup[]) {

        const inferredEdgePrefix = "_inferred_";
        // Iterate the full list and skip non-`draw` directives inline, so specIndex
        // stays a position in directives.inferredEdges rather than in a filtered copy.
        this._layoutSpec.directives.inferredEdges.forEach((he, specIndex) => {
            if (!he.draw) {
                return;
            }

            const draw = he.draw!;

            // Check the named group ends against the groups that were actually
            // built, once per directive. Parsing only warns about a name no group
            // constraint defines, so a name may still be unresolved here — it
            // lands in the "no groups exist" skip below. What kind of groups a
            // constraint builds (one per key, or a single unkeyed group) is in
            // any case only knowable per instance.
            const namedEnds = [...new Set([draw.source, draw.target])]
                .filter((end): end is string => end !== null);
            const builtFor = (endName: string) => groups.filter(grp =>
                grp.sourceConstraint !== undefined
                && (grp.sourceConstraint as GroupBySelector).name === endName);
            for (const endName of namedEnds) {
                const built = builtFor(endName);
                const single = built.filter(grp => grp.keyed !== true);
                // A name may mean "one group per key" (binary selector) or "the
                // single group" (unary selector) — but not both at once, and not
                // two single groups: with no key in play there is no way to pick.
                if (single.length > 1) {
                    throw new Error(
                        `inferredEdge '${he.name}': draw endpoint '${endName}' is ambiguous — `
                        + `${single.length} group constraints named '${endName}' each build a single (unkeyed) group. `
                        + `Rename one of them.`
                    );
                }
                if (single.length === 1 && built.length > 1) {
                    throw new Error(
                        `inferredEdge '${he.name}': draw endpoint '${endName}' is ambiguous — '${endName}' names `
                        + `both a keyed group constraint (one group per key) and a single-group (unary) constraint. `
                        + `Rename one of them.`
                    );
                }
            }
            for (const endName of namedEnds) {
                // No groups at all. Indistinguishable from an empty relation in
                // this instance (which also builds no groups), so this is a data
                // fact, not a spec bug: warn once and skip the directive.
                if (builtFor(endName).length === 0) {
                    console.warn(`[spytial] inferredEdge '${he.name}': skipping — no '${endName}' groups exist in this instance.`);
                    return;
                }
            }
            let res;
            try {
                res = this.evaluator.evaluate(he.selector, { instanceIndex: this.instanceNum });
            } catch (error) {
                this.recordSelectorError(he.selector, 'inferredEdge selector', error);
                return; // Skip this inferred edge
            }
            if (!this.acceptSelectorResult(res, he.selector, 'inferredEdge selector', 'any', 'inferredEdge', specIndex)) {
                return; // Skip this directive only — every other one still applies
            }

            // `draw` also supports unary selectors: the single atom feeds both
            // ends (e.g. `draw: _ -> regions` connects each key to its own group).
            let selectedTuples: string[][];
            if (res.maxArity() > 1) {
                selectedTuples = res.selectedTuplesAll();
            } else {
                selectedTuples = res.selectedAtoms().map((atom: string) => [atom]);
            }

            let edgeIdPrefix = `${inferredEdgePrefix}<:${he.name}`;

            selectedTuples.forEach((tuple) => {

                let n = tuple.length;
                const sourceAtom = tuple[0];
                const targetAtom = tuple[n - 1];

                const source = this.resolveDrawEnd(g, groups, draw.source, sourceAtom, he.name);
                const target = this.resolveDrawEnd(g, groups, draw.target, targetAtom, he.name);
                if (!source || !target) {
                    return; // Skipped; resolveDrawEnd already warned.
                }

                // Both ends resolving to the same group is fine: the anchors
                // coincide on one member and the renderer draws a self-loop on
                // the hull, just like a node self-loop.

                let edgeLabel = he.name;
                if (n > 2) {
                    let middleNodeLabels = tuple.slice(1, n - 1).map(nodeId => {
                        const nodeMetadata = g.node(nodeId);
                        return nodeMetadata?.label || nodeId;
                    }).join(",");
                    edgeLabel = `${edgeLabel}[${middleNodeLabels}]`;
                }

                let fullTuple = tuple.join("->");
                let edgeId = `${edgeIdPrefix}<:${fullTuple}`;
                g.setEdge(source.anchor, target.anchor, edgeLabel, edgeId);

                if (source.groupId || target.groupId) {
                    this.inferredEdgeGroupStamps.set(edgeId, {
                        sourceGroupId: source.groupId,
                        targetGroupId: target.groupId
                    });
                }
            });
        });
    }

    /**
     * Resolves one `draw` endpoint to an anchor node (and, for group ends, the
     * group to attach to). Returns null when the endpoint can't be resolved in
     * this instance — a data-dependent skip, reported via console.warn.
     */
    private resolveDrawEnd(
        g: Graph,
        groups: LayoutGroup[],
        family: string | null,
        atom: string,
        directiveName: string
    ): { anchor: string; groupId?: string } | null {

        if (family === null) {
            // Plain atom end. Unlike plain inferred edges (where graphlib would
            // silently materialise a phantom node), check presence explicitly.
            if (!g.hasNode(atom)) {
                console.warn(`[spytial] inferredEdge '${directiveName}': skipping edge — endpoint atom '${atom}' is not in the graph (hidden or projected away).`);
                return null;
            }
            return { anchor: atom };
        }

        const named = groups.filter(grp =>
            grp.sourceConstraint !== undefined
            && (grp.sourceConstraint as GroupBySelector).name === family
        );

        // A unary group constraint builds a single group with no keys: the end
        // attaches to that group, whatever the atom. (addDrawInferredEdges has
        // already rejected names that mean both a keyed and a single group.)
        const single = named.find(grp => grp.keyed !== true);
        if (single) {
            const singleAnchor = single.nodeIds.find(id => g.hasNode(id));
            if (!singleAnchor) {
                console.warn(`[spytial] inferredEdge '${directiveName}': skipping edge — group '${single.name}' has no members present in the graph to anchor on.`);
                return null;
            }
            return { anchor: singleAnchor, groupId: single.name };
        }

        const matches = named.filter(grp => grp.keyed === true && grp.keyNodeId === atom);

        if (matches.length === 0) {
            console.warn(`[spytial] inferredEdge '${directiveName}': skipping edge — atom '${atom}' does not key any '${family}' group in this instance.`);
            return null;
        }
        if (matches.length > 1) {
            // A true (family, key) collision: two group constraints with the same
            // name produced groups keyed by the same atom. Genuinely ambiguous.
            throw new Error(`inferredEdge '${directiveName}': ambiguous draw endpoint — atom '${atom}' keys ${matches.length} groups named '${family}[…]'. Rename one of the group constraints.`);
        }

        const group = matches[0];
        // Anchor on a member that is actually in the graph. Members are what the
        // hull is drawn around, so (unlike the key) one is normally present.
        const memberAnchor = group.nodeIds.find(id => g.hasNode(id));
        if (!memberAnchor) {
            console.warn(`[spytial] inferredEdge '${directiveName}': skipping edge — group '${group.name}' has no members present in the graph to anchor on.`);
            return null;
        }
        return { anchor: memberAnchor, groupId: group.name };
    }
}
